import { useEffect, useState, useRef } from "react";
import { adminAPI, connectAPI, pointsAPI, achievementsAPI, shippingAPI, priceCompareAPI, priceCompareAdminAPI, catalogAPI, shippingLabelAPI, shippingAdminAPI, paymentsAPI, adminSimhubAPI, forumSpotlightAdminAPI, sponsorAdminAPI, legalAPI, moderationAPI, achievementConfigsAPI, mediaAPI, resolveMediaUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { memberCategoryLabel } from "@/lib/memberCategory";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Trash2, Flag, ShieldOff, ShieldCheck, Ban, Check, X, Upload, Download, Plus, Save, Pencil, Truck, Copy, MessageSquare, Search as SearchIcon, UserCog, UserPlus, FileText, Send, Image } from "lucide-react";
import OrderStatusTimeline from "@/components/OrderStatusTimeline";
import { EQUIPMENT_FIELDS } from "@/lib/constants";
import PriceCompareAnalytics from "@/components/admin/PriceCompareAnalytics";

const useList = (fetcher, deps = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = () => {
    setLoading(true);
    fetcher().then((res) => setData(res)).catch(() => setData([])).finally(() => setLoading(false));
  };
  useEffect(() => reload(), deps);
  return { data, loading, reload };
};

const Loading = () => <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>;
const Empty = ({ text }) => <p className="py-10 text-center text-sm text-muted-foreground">{text}</p>;

const act = async (fn, msg, reload) => {
  try { await fn(); toast.success(msg); reload?.(); }
  catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
};

/* ---------------- Listings moderation ---------------- */
export function AdminListings() {
  const { data, loading, reload } = useList(() => adminAPI.getListings({ limit: 50 }).then((r) => r.data?.listings || []));
  if (loading) return <Loading />;
  if (!data.length) return <Empty text="No listings." />;
  return (
    <div className="space-y-2">
      {data.map((l) => (
        <Card key={l.id} className="flex items-center justify-between gap-3 border-border bg-card p-3" data-testid={`admin-listing-${l.id}`}>
          <div className="min-w-0">
            <p className="truncate font-medium">{l.title} {l.is_flagged && <Flag className="ml-1 inline h-3.5 w-3.5 text-destructive" />}</p>
            <p className="text-xs text-muted-foreground">${l.price} · {l.seller_username} · <span className="capitalize">{l.status}</span></p>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button size="sm" variant="outline" onClick={() => act(() => adminAPI.flagListing(l.id, !l.is_flagged), "Flag updated", reload)}><Flag className="h-4 w-4" /></Button>
            <Button size="sm" variant="destructive" onClick={() => act(() => adminAPI.deleteListing(l.id), "Listing deleted", reload)} data-testid={`admin-listing-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Forum moderation ---------------- */
export function AdminForum() {
  const { data, loading, reload } = useList(() => adminAPI.getForumPosts({ limit: 50 }).then((r) => r.data?.posts || []));
  if (loading) return <Loading />;
  if (!data.length) return <Empty text="No forum posts." />;
  return (
    <div className="space-y-2">
      {data.map((p) => (
        <Card key={p.id} className="flex items-center justify-between gap-3 border-border bg-card p-3">
          <div className="min-w-0">
            <p className="truncate font-medium">{p.title} {p.is_flagged && <Flag className="ml-1 inline h-3.5 w-3.5 text-destructive" />}</p>
            <p className="text-xs text-muted-foreground">{p.author_username} · {p.reply_count} replies</p>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button size="sm" variant="outline" onClick={() => act(() => adminAPI.flagForumPost(p.id, !p.is_flagged), "Flag updated", reload)}><Flag className="h-4 w-4" /></Button>
            <Button size="sm" variant="destructive" onClick={() => act(() => adminAPI.deleteForumPost(p.id), "Post deleted", reload)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Social moderation ---------------- */
const ACH_TYPE_LABELS = {
  first_session: "First Session",
  top_speed: "Top Speed",
  track_pb: "Track Personal Best",
  track_first: "First Lap on New Track",
  laps: "Lap Milestones",
  leaderboard: "Leaderboard Position",
};

function AdminAchievementStyleRow({ type, cfg, onSaved }) {
  const [title, setTitle] = useState(cfg.title || "");
  const [subtitle, setSubtitle] = useState(cfg.subtitle || "");
  const [bg, setBg] = useState(cfg.background_image || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = () => act(() => achievementConfigsAPI.update(type, { title, subtitle }), "Saved", onSaved);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await achievementConfigsAPI.uploadBackground(type, fd);
      setBg(r.data?.background_image || "");
      toast.success("Background updated");
      onSaved?.();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const bgUrl = bg ? `${process.env.REACT_APP_BACKEND_URL}${bg}` : null;

  return (
    <Card className="space-y-3 border-border bg-card p-3" data-testid={`ach-style-${type}`}>
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary bg-cover bg-center"
          style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        >
          {!bgUrl && <Image className="h-4 w-4 text-muted-foreground" />}
        </div>
        <p className="text-sm font-bold">{ACH_TYPE_LABELS[type] || type}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={save} data-testid={`ach-title-${type}`} />
        </div>
        <div>
          <Label className="text-xs">Subtitle</Label>
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} onBlur={save} data-testid={`ach-subtitle-${type}`} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={upload} data-testid={`ach-bg-input-${type}`} />
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`ach-bg-upload-${type}`}>
          {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Background
        </Button>
        {bg && <Button size="sm" variant="ghost" onClick={() => act(() => achievementConfigsAPI.update(type, { background_image: "" }), "Background cleared", () => { setBg(""); onSaved?.(); })} data-testid={`ach-bg-clear-${type}`}>Clear</Button>}
      </div>
    </Card>
  );
}

function AdminAchievementStyles() {
  const [configs, setConfigs] = useState(null);
  const load = () => achievementConfigsAPI.list().then((r) => setConfigs(r.data)).catch(() => setConfigs({}));
  useEffect(() => { load(); }, []);
  return (
    <Card className="space-y-3 border-border bg-card p-4">
      <div>
        <p className="text-sm font-semibold">Achievement post styles</p>
        <p className="text-xs text-muted-foreground">Customise the background, title and subtitle shown on each type of achievement post.</p>
      </div>
      {!configs ? <Loading /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.keys(ACH_TYPE_LABELS).map((t) => (
            <AdminAchievementStyleRow key={t} type={t} cfg={configs[t] || {}} onSaved={load} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function AdminSocial() {
  const { data, loading, reload } = useList(() => adminAPI.getSocialPosts({ limit: 50 }).then((r) => r.data?.posts || []));
  return (
    <div className="space-y-4">
      <AdminAchievementStyles />
      {loading ? <Loading /> : !data.length ? <Empty text="No social posts." /> : (
        <div className="space-y-2">
          {data.map((p) => (
            <Card key={p.id} className="flex items-center justify-between gap-3 border-border bg-card p-3">
              <div className="min-w-0"><p className="truncate">{p.content}</p><p className="text-xs text-muted-foreground">{p.author_username} · {p.likes?.length || 0} likes</p></div>
              <Button size="sm" variant="destructive" onClick={() => act(() => adminAPI.deleteSocialPost(p.id), "Post deleted", reload)}><Trash2 className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Sponsorships ---------------- */
const TIER_META = {
  grassroots: { label: "Grassroots", color: "bg-slate-500/20 text-slate-300" },
  rising: { label: "Rising", color: "bg-amber-500/20 text-amber-400" },
  pro: { label: "Pro", color: "bg-primary/20 text-primary" },
  academy: { label: "SRC Academy", color: "bg-gradient-to-r from-yellow-500/25 to-amber-500/25 text-yellow-200" },
};

export function AdminSponsorships() {
  const { data, loading, reload } = useList(() => adminAPI.getSponsorships({ limit: 100 }).then((r) => r.data?.sponsorships || r.data?.requests || (Array.isArray(r.data) ? r.data : [])));
  const [q, setQ] = useState("");
  if (loading) return <Loading />;
  if (!data.length) return <Empty text="No sponsorship requests." />;

  // Show apps with new info first, then pending, then rest.
  const sorted = [...data].sort((a, b) => {
    const w = (r) => (r.has_pending_updates ? 0 : r.status === "pending" ? 1 : 2);
    return w(a) - w(b);
  });

  const term = q.trim().toLowerCase();
  const filtered = term
    ? sorted.filter((s) =>
        [s.user_username, s.real_name, s.platform, s.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
      )
    : sorted;

  return (
    <div className="space-y-3">
      <SponsorshipTermsEditor />
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search sponsored users by name, @username, platform or status..."
          className="pl-9"
          data-testid="admin-sponsor-search"
        />
      </div>
      {!filtered.length ? (
        <Empty text="No sponsored users match your search." />
      ) : (
        filtered.map((s) => <AdminSponsorshipCard key={s.id} req={s} onDone={reload} />)
      )}
    </div>
  );
}

function SponsorshipTermsEditor() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    adminAPI.getSponsorshipTerms()
      .then((r) => setContent(r.data?.content || ""))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await adminAPI.updateSponsorshipTerms(content);
      toast.success("Sponsorship Terms saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save (owner only)");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-border bg-card p-4" data-testid="admin-sponsorship-terms">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between">
        <span className="font-display text-sm font-semibold">Sponsorship Terms (global)</span>
        <span className="text-xs text-primary">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">Shown in every deal and at /sponsorship-terms. Only the owner can save changes.</p>
          {loading ? <Skeleton className="h-28 w-full" /> : (
            <textarea
              className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm"
              rows={8}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter the global Sponsorship Terms..."
              data-testid="admin-sponsorship-terms-text"
            />
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={busy} className="bg-primary text-primary-foreground" data-testid="admin-sponsorship-terms-save">
              <Save className="mr-1 h-3.5 w-3.5" /> Save terms
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function AdminSponsorshipCard({ req, onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [rejectText, setRejectText] = useState("");
  const [showDeal, setShowDeal] = useState(false);
  const [dealDetails, setDealDetails] = useState(req.deal?.details || "");
  const [dealTier, setDealTier] = useState(req.deal?.tier || req.tier || "grassroots");
  const [dealBrands, setDealBrands] = useState(req.deal?.brand_ids || req.sponsors || []);
  const [pool, setPool] = useState([]);
  const tier = TIER_META[req.tier || "grassroots"];
  const upgradeReq = req.tier_upgrade_request;
  const socials = Array.isArray(req.social_links) ? req.social_links.filter(Boolean) : [];
  const showUpdatesBadge = req.has_pending_updates;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openDeal = async () => {
    setShowDeal(true);
    try {
      const r = await sponsorAdminAPI.listSponsors();
      setPool(Array.isArray(r.data) ? r.data : (r.data?.sponsors || []));
    } catch { /* ignore */ }
  };

  const toggleBrand = (id) => setDealBrands((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const sendDeal = async () => {
    if (!dealDetails.trim()) { toast.error("Enter the sponsorship offer details"); return; }
    await run(() => adminAPI.sendSponsorshipDeal(req.id, { details: dealDetails.trim(), tier: dealTier, brand_ids: dealBrands }), "Sponsorship deal sent");
    setShowDeal(false);
  };

  const onStatusChange = (v) => {
    if (v === "approved") { openDeal(); return; }
    run(() => adminAPI.updateSponsorshipStatus(req.id, v), "Status updated");
  };

  const sendMessage = async () => {
    if (!msgText.trim()) { toast.error("Enter a message"); return; }
    await run(() => adminAPI.sendSponsorshipMessage(req.id, msgText.trim()), "Message sent");
    setMsgText(""); setShowMsg(false);
  };

  const rejectUpdate = async () => {
    await run(() => adminAPI.rejectSponsorshipUpdates(req.id, rejectText.trim()), "Update rejected");
    setRejectText(""); setShowReject(false);
  };

  return (
    <Card className={`space-y-3 border-border bg-card p-4 ${showUpdatesBadge ? "ring-1 ring-primary" : ""}`} data-testid={`admin-sponsor-${req.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold">{req.real_name || req.user_username}</p>
            <Badge className={`border-0 ${tier.color}`} data-testid={`admin-sponsor-tier-${req.id}`}>
              {tier.label}
            </Badge>
            {showUpdatesBadge && (
              <Badge className="border-0 bg-primary text-primary-foreground animate-pulse" data-testid={`admin-sponsor-newinfo-${req.id}`}>
                NEW INFO
              </Badge>
            )}
            {upgradeReq && (
              <Badge className="border-0 bg-amber-500/20 text-amber-400" data-testid={`admin-sponsor-upgrade-req-${req.id}`}>
                Upgrade → {TIER_META[upgradeReq.target_tier]?.label || upgradeReq.target_tier}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            @{req.user_username} · {(req.platforms?.length ? req.platforms.map((p) => p.platform).join(", ") : req.platform)} · <span className="capitalize">{req.status}</span>
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)} data-testid={`admin-sponsor-toggle-${req.id}`}>
          {expanded ? "Hide" : "Details"}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-border pt-3 text-sm">
          {(req.platforms?.length ? req.platforms : [{
            platform: req.platform, license_level: req.license_level, account_link: req.account_link,
            livery_image: req.livery_image, livery_file_name: req.livery_file_name, livery_description: req.livery_description,
            podiums_wins: req.podiums_wins, wins: req.wins, tournaments: req.tournaments,
            username: req.username, profile_screenshot: req.profile_screenshot,
          }]).map((p, pi) => (
            <div key={pi} className="space-y-2 rounded-md border border-border bg-secondary/30 p-3" data-testid={`admin-sponsor-platform-${pi}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">{p.platform || "Platform"} · {p.license_level || "—"}</p>
              {p.username && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Username / profile name</p><p>{p.username}</p></div>
              )}
              {p.profile_screenshot && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Driver profile screenshot</p>
                  <img src={p.profile_screenshot} alt="Driver profile" className="max-h-48 w-full rounded-md object-cover" />
                </div>
              )}
              {p.livery_image && (
                p.livery_image.startsWith("data:image/") ? (
                  <img src={p.livery_image} alt="Livery" className="max-h-48 w-full rounded-md object-cover" />
                ) : (
                  <a href={p.livery_image} download={p.livery_file_name || "livery"} className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-primary" data-testid={`admin-sponsor-livery-file-${pi}`}>
                    <FileText className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{p.livery_file_name || "Livery file"}</span><Download className="h-4 w-4 shrink-0" />
                  </a>
                )
              )}
              {p.livery_description && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Livery plans</p><p>{p.livery_description}</p></div>
              )}
              {(p.wins?.length ? (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Wins / Notable Tournaments</p>
                  <ul className="list-inside list-disc">
                    {p.wins.map((w, wi) => <li key={wi}>{w.name}{w.position ? ` — ${w.position}` : ""}</li>)}
                  </ul>
                </div>
              ) : p.podiums_wins && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Wins / Notable Tournaments</p><p>{p.podiums_wins}</p></div>
              ))}
              {p.tournaments && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Upcoming Tournaments / Leagues</p><p>{p.tournaments}</p></div>
              )}
              {p.account_link && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Account link</p>
                  <a href={p.account_link} target="_blank" rel="noreferrer" className="text-primary break-all">{p.account_link}</a>
                </div>
              )}
            </div>
          ))}
          {socials.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Socials</p>
              <ul className="mt-1 list-inside list-disc text-primary">
                {socials.map((s, i) => {
                  const label = typeof s === "object" ? `${s.platform}: ${s.handle}` : s;
                  const href = typeof s === "object" ? s.handle : s;
                  return <li key={i}><a href={href} target="_blank" rel="noreferrer" className="break-all">{label}</a></li>;
                })}
              </ul>
            </div>
          )}
          {Array.isArray(req.proposal_files) && req.proposal_files.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Proposal files</p>
              <ul className="mt-1 space-y-1">
                {req.proposal_files.map((f, i) => (
                  <li key={i}>
                    <a href={f.data} download={f.file_name || "proposal"} className="inline-flex items-center gap-2 text-primary" data-testid={`admin-sponsor-proposal-${i}`}>
                      <FileText className="h-4 w-4 shrink-0" /><span className="break-all">{f.label ? `${f.label}: ` : ""}{f.file_name || "file"}</span><Download className="h-4 w-4 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {req.additional_info && (
            <div><p className="text-xs font-semibold uppercase text-muted-foreground">Additional info</p><p className="whitespace-pre-wrap">{req.additional_info}</p></div>
          )}
          {req.rig_equipment && Object.values(req.rig_equipment).some(Boolean) && (
            <div data-testid={`admin-sponsor-rig-${req.id}`}>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Rig / setup</p>
              <ul className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
                {EQUIPMENT_FIELDS.filter((f) => req.rig_equipment[f.key]).map((f) => (
                  <li key={f.key} className="text-sm"><span className="text-muted-foreground">{f.label}:</span> {req.rig_equipment[f.key]}</li>
                ))}
              </ul>
            </div>
          )}
          {upgradeReq?.note && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-300">
              Upgrade note: {upgradeReq.note}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Select value={req.status} onValueChange={onStatusChange}>
          <SelectTrigger className="w-40" data-testid={`admin-sponsor-status-${req.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {["pending", "reviewing", "deal_offered", "approved", "rejected", "active"].map((st) => <SelectItem key={st} value={st}>{st === "deal_offered" ? "deal offered" : st}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button size="sm" className="bg-primary text-primary-foreground" onClick={openDeal} data-testid={`admin-sponsor-send-deal-${req.id}`} disabled={busy}>
          <Send className="mr-1 h-3.5 w-3.5" /> {req.deal ? "Edit / resend deal" : "Send deal"}
        </Button>

        <Select value={req.tier || "grassroots"} onValueChange={(v) => run(() => adminAPI.setSponsorshipTier(req.id, v), `Tier set to ${v}`)}>
          <SelectTrigger className="w-36" data-testid={`admin-sponsor-tier-select-${req.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {["grassroots", "rising", "pro", "academy"].map((t) => <SelectItem key={t} value={t}>{TIER_META[t].label}</SelectItem>)}
          </SelectContent>
        </Select>

        {upgradeReq && (
          <Button size="sm" variant="outline" onClick={() => run(() => adminAPI.denySponsorshipTier(req.id), "Upgrade request cleared")} data-testid={`admin-sponsor-deny-upgrade-${req.id}`} disabled={busy}>
            Deny upgrade
          </Button>
        )}
        {showUpdatesBadge && (
          <Button size="sm" variant="outline" onClick={() => run(() => adminAPI.clearSponsorshipUpdates(req.id), "Marked as reviewed")} data-testid={`admin-sponsor-clear-updates-${req.id}`} disabled={busy}>
            Mark reviewed
          </Button>
        )}
        {showUpdatesBadge && (
          <Button size="sm" variant="outline" onClick={() => setShowReject((v) => !v)} data-testid={`admin-sponsor-reject-updates-toggle-${req.id}`} disabled={busy} className="border-destructive/50 text-destructive hover:bg-destructive/10">
            Reject update
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => setShowMsg((v) => !v)} data-testid={`admin-sponsor-message-toggle-${req.id}`} disabled={busy}>
          <MessageSquare className="mr-1 h-3.5 w-3.5" /> Message user
        </Button>
      </div>

      {showReject && (
        <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid={`admin-sponsor-reject-box-${req.id}`}>
          <p className="text-xs text-muted-foreground">Optionally tell the user why their update wasn't approved.</p>
          <textarea
            className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm"
            rows={2}
            placeholder="Reason (optional)"
            value={rejectText}
            onChange={(e) => setRejectText(e.target.value)}
            data-testid={`admin-sponsor-reject-text-${req.id}`}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button size="sm" onClick={rejectUpdate} disabled={busy} className="bg-destructive text-destructive-foreground" data-testid={`admin-sponsor-reject-send-${req.id}`}>Reject update</Button>
          </div>
        </div>
      )}

      {showDeal && (
        <div className="space-y-3 rounded-md border border-primary/40 bg-primary/5 p-3" data-testid={`admin-sponsor-deal-box-${req.id}`}>
          <p className="text-sm font-semibold">Send sponsorship deal</p>
          <p className="text-xs text-muted-foreground">
            The applicant must accept the Sponsorship Terms before this becomes active. The sponsorship agreement includes the global Terms plus the details you provide below.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Offer details *</Label>
            <textarea
              className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm"
              rows={4}
              placeholder="Describe the sponsorship offer, deliverables, and expectations..."
              value={dealDetails}
              onChange={(e) => setDealDetails(e.target.value)}
              data-testid={`admin-sponsor-deal-details-${req.id}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sponsorship tier</Label>
            <Select value={dealTier} onValueChange={setDealTier}>
              <SelectTrigger className="w-40" data-testid={`admin-sponsor-deal-tier-${req.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["grassroots", "rising", "pro", "academy"].map((t) => <SelectItem key={t} value={t}>{TIER_META[t].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Included brands (from Sponsor Logos)</Label>
            {!pool.length ? (
              <p className="text-xs text-muted-foreground">No sponsor logos defined yet — add them under Sponsor Logos.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {pool.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleBrand(s.id)}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${dealBrands.includes(s.id) ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground"}`}
                    data-testid={`admin-sponsor-deal-brand-${s.id}`}
                  >
                    {s.logo_url && <img src={s.logo_url} alt="" className="h-4 w-4 rounded object-contain" />}
                    {s.name}
                    {dealBrands.includes(s.id) && <Check className="h-3 w-3 text-primary" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowDeal(false)}>Cancel</Button>
            <Button size="sm" onClick={sendDeal} disabled={busy} className="bg-primary text-primary-foreground" data-testid={`admin-sponsor-deal-send-${req.id}`}>
              <Send className="mr-1 h-3.5 w-3.5" /> Send deal
            </Button>
          </div>
        </div>
      )}

      {showMsg && (
        <div className="space-y-2 rounded-md border border-border bg-secondary/20 p-3" data-testid={`admin-sponsor-message-box-${req.id}`}>
          <textarea
            className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm"
            rows={2}
            placeholder={`Message @${req.user_username}...`}
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            data-testid={`admin-sponsor-message-text-${req.id}`}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowMsg(false)}>Cancel</Button>
            <Button size="sm" onClick={sendMessage} disabled={busy} className="bg-primary text-primary-foreground" data-testid={`admin-sponsor-message-send-${req.id}`}>Send</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ---------------- Announcements ---------------- */
export function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "", announcement_type: "general", link_url: "", link_text: "", priority: 0, send_message: false, message_body: "" });

  const reload = () => {
    setLoading(true);
    adminAPI.listAnnouncements()
      .then((r) => setItems(Array.isArray(r.data) ? r.data : r.data?.announcements || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!draft.title.trim() || !draft.content.trim()) { toast.error("Title and content are required"); return; }
    setCreating(true);
    try {
      await adminAPI.createAnnouncement(draft);
      toast.success(draft.send_message ? "Announcement published & message sent to all members" : "Announcement published");
      setDraft({ title: "", content: "", announcement_type: "general", link_url: "", link_text: "", priority: 0, send_message: false, message_body: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider">New announcement</h3>
        <div className="space-y-3">
          <Input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="announcement-title" />
          <textarea
            className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm"
            rows={3}
            placeholder="Content"
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            data-testid="announcement-content"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={draft.announcement_type} onValueChange={(v) => setDraft({ ...draft, announcement_type: v })}>
              <SelectTrigger data-testid="announcement-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["general", "event", "maintenance", "feature", "warning"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Priority (higher = pinned)" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: parseInt(e.target.value) || 0 })} data-testid="announcement-priority" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input placeholder="Link URL (optional)" value={draft.link_url} onChange={(e) => setDraft({ ...draft, link_url: e.target.value })} />
            <Input placeholder="Link text (optional)" value={draft.link_text} onChange={(e) => setDraft({ ...draft, link_text: e.target.value })} />
          </div>
          <div className="rounded-md border border-border bg-secondary/20 p-3">
            <label className="flex items-start gap-2 text-sm" htmlFor="announcement-send-message">
              <input
                id="announcement-send-message"
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={draft.send_message}
                onChange={(e) => setDraft({ ...draft, send_message: e.target.checked })}
                data-testid="announcement-send-message"
              />
              <span>
                <span className="font-medium">Also send as a message to all members</span>
                <span className="block text-xs text-muted-foreground">Delivers a full message to every member&apos;s inbox from your account. Great for longer notices than a push notification. Tapping their notification opens the message with a &quot;Read Message&quot; prompt.</span>
              </span>
            </label>
            {draft.send_message && (
              <textarea
                className="mt-3 w-full rounded-md border border-border bg-background p-2 text-sm"
                rows={4}
                placeholder="Full message to send to members (leave blank to reuse the content above)"
                value={draft.message_body}
                onChange={(e) => setDraft({ ...draft, message_body: e.target.value })}
                data-testid="announcement-message-body"
              />
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={creating} className="bg-primary text-primary-foreground" data-testid="announcement-publish">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Publish</>}
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider">All announcements ({items.length})</h3>
          <Button size="sm" variant="outline" onClick={reload}>Refresh</Button>
        </div>
        {loading ? <Loading /> : !items.length ? <Empty text="No announcements yet." /> : (
          <div className="space-y-2">
            {items.map((a) => <AnnouncementRow key={a.id} item={a} onDone={reload} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function AnnouncementRow({ item, onDone }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: item.title || "",
    content: item.content || "",
    announcement_type: item.announcement_type || "general",
    priority: item.priority || 0,
    link_url: item.link_url || "",
    link_text: item.link_text || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await adminAPI.updateAnnouncement(item.id, draft);
      toast.success("Announcement updated");
      setEditing(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const toggle = () => act(() => adminAPI.toggleAnnouncement(item.id), item.is_active ? "Hidden from users" : "Visible to users", onDone);
  const remove = () => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    act(() => adminAPI.deleteAnnouncement(item.id), "Announcement deleted", onDone);
  };

  return (
    <Card className="border-border bg-card p-3" data-testid={`announcement-row-${item.id}`}>
      {editing ? (
        <div className="space-y-2">
          <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <textarea className="w-full rounded-md border border-border bg-secondary/40 p-2 text-sm" rows={3} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
          <div className="grid gap-2 sm:grid-cols-2">
            <Select value={draft.announcement_type} onValueChange={(v) => setDraft({ ...draft, announcement_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["general", "event", "maintenance", "feature", "warning"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: parseInt(e.target.value) || 0 })} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder="Link URL" value={draft.link_url} onChange={(e) => setDraft({ ...draft, link_url: e.target.value })} />
            <Input placeholder="Link text" value={draft.link_text} onChange={(e) => setDraft({ ...draft, link_text: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" className="bg-primary text-primary-foreground" onClick={save} disabled={busy} data-testid={`announcement-save-${item.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">{item.title}</p>
              <Badge className={`border-0 ${item.is_active ? "bg-emerald-500/20 text-emerald-400" : "bg-muted text-muted-foreground"}`} data-testid={`announcement-status-${item.id}`}>
                {item.is_active ? "Active" : "Hidden"}
              </Badge>
              <Badge className="border-0 bg-secondary text-muted-foreground text-[10px]">{item.announcement_type}</Badge>
              {item.priority > 0 && <Badge className="border-0 bg-primary/20 text-primary text-[10px]">#{item.priority}</Badge>}
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{item.content}</p>
          </div>
          <div className="flex flex-shrink-0 flex-col gap-1">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`announcement-edit-${item.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={toggle} data-testid={`announcement-toggle-${item.id}`}>
              {item.is_active ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="destructive" onClick={remove} data-testid={`announcement-delete-${item.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ---------------- Payouts ---------------- */
export function AdminPayouts() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const reload = () => {
    setLoading(true);
    paymentsAPI.getMyOrders({ role: "admin" })
      .then((r) => setOrders(r.data?.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const filtered = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "pending_payout") return (
      (o.escrow_status === "held" && ["paid", "shipped"].includes(o.status)) ||
      o.escrow_status === "pending_manual_payout"
    );
    if (filter === "released") return o.escrow_status === "released";
    if (filter === "disputed") return o.escrow_status === "disputed" || !!o.dispute_reason;
    if (filter === "manual") return o.escrow_status === "pending_manual_payout";
    return true;
  });

  if (loading) return <Loading />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", l: "All" },
          { k: "pending_payout", l: "Awaiting payout" },
          { k: "manual", l: "Manual payout" },
          { k: "disputed", l: "Disputed" },
          { k: "released", l: "Released" },
        ].map((tab) => (
          <Button
            key={tab.k}
            variant={filter === tab.k ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(tab.k)}
            className={filter === tab.k ? "bg-primary text-primary-foreground" : ""}
            data-testid={`admin-payouts-filter-${tab.k}`}
          >
            {tab.l}
          </Button>
        ))}
        <div className="ml-auto">
          <Button size="sm" variant="outline" onClick={reload}>Refresh</Button>
        </div>
      </div>

      {!filtered.length ? <Empty text="No orders in this view." /> : (
        <div className="space-y-3">
          {filtered.map((o) => <AdminPayoutRow key={o.id} order={o} onDone={reload} />)}
        </div>
      )}
    </div>
  );
}

function AdminPayoutRow({ order, onDone }) {
  const copyTracking = () => {
    if (!order.tracking_number) return;
    navigator.clipboard?.writeText(order.tracking_number);
    toast.success("Tracking copied");
  };
  const displayAmount = ((order.amount || 0) / 100).toFixed(2);
  const canRelease = order.escrow_status === "held" || order.escrow_status === "pending_manual_payout";
  const canRefund = order.escrow_status === "held" || order.escrow_status === "disputed";
  const readyForPayout = order.escrow_status === "pending_manual_payout";

  return (
    <Card className="border-border bg-card p-4" data-testid={`admin-payout-${order.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{order.listing_title || "Order"}</p>
          <p className="text-xs text-muted-foreground">
            ${displayAmount} · {order.buyer_username || order.buyer_id} → {order.seller_username || order.seller_id}
            {order.created_at && <> · {new Date(order.created_at).toLocaleDateString()}</>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className="border-0 bg-secondary capitalize">{order.escrow_status || order.status || "—"}</Badge>
          {order.tracking_number && (
            <button
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
              onClick={copyTracking}
              data-testid={`admin-payout-tracking-${order.id}`}
            >
              <Truck className="h-3 w-3" />
              <span className="font-mono">{order.tracking_number}</span>
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <OrderStatusTimeline order={order} className="mt-3" />

      {readyForPayout && (
        <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs text-emerald-400">
          Buyer confirmed delivery. Review and mark as paid out to release funds to the seller.
        </div>
      )}

      {order.dispute_reason && (
        <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-400">
          Dispute: {order.dispute_reason}
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {canRelease && (
          <Button
            size="sm"
            className={readyForPayout ? "bg-primary text-primary-foreground" : ""}
            variant={readyForPayout ? "default" : "outline"}
            onClick={() => act(() => paymentsAPI.adminMarkPaidOut(order.id), "Payout released to seller", onDone)}
            data-testid={`admin-payout-release-${order.id}`}
          >
            {readyForPayout ? "Mark as paid out" : "Release payout"}
          </Button>
        )}
        {order.escrow_status === "held" && (
          <Button size="sm" variant="outline" onClick={() => act(() => connectAPI.adminHoldPayout(order.id), "Payout held", onDone)}>
            Hold
          </Button>
        )}
        {canRefund && (
          <Button size="sm" variant="destructive" onClick={() => act(() => connectAPI.adminRefundPayout(order.id), "Refunded", onDone)} data-testid={`admin-payout-refund-${order.id}`}>
            Refund
          </Button>
        )}
      </div>
    </Card>
  );
}

/* ---------------- Points admin ---------------- */
export function AdminPoints() {
  const [tab, setTab] = useState("actions");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 p-0.5 text-xs">
        {[
          { k: "actions", label: "Action Values" },
          { k: "rewards", label: "Rewards" },
          { k: "redemptions", label: "Redemption Requests" },
        ].map((t) => (
          <button
            key={t.k}
            className={`rounded px-3 py-1.5 ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            onClick={() => setTab(t.k)}
            data-testid={`admin-points-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "actions" && <AdminPointsActions />}
      {tab === "rewards" && <AdminPointsRewards />}
      {tab === "redemptions" && <AdminPointsRedemptions />}
    </div>
  );
}

function AdminPointsActions() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    pointsAPI.adminActionValues().then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (row) => {
    const val = edits[row.action];
    if (val === undefined) return;
    await act(() => pointsAPI.adminUpdateActionValue(row.action, Number(val)), "Saved", load);
    setEdits((p) => { const { [row.action]: _drop, ...rest } = p; return rest; });
  };

  if (loading) return <Loading />;

  const grouped = rows.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Set how many points each action awards. Set a value to 0 to disable an action.</p>
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">{cat}</h4>
          <Card className="divide-y divide-border border-border bg-card">
            {list.map((row) => {
              const pending = edits[row.action];
              const val = pending !== undefined ? pending : row.current_points;
              return (
                <div key={row.action} className="grid grid-cols-[1fr_120px_80px] items-center gap-3 p-3" data-testid={`admin-points-action-${row.action}`}>
                  <div>
                    <p className="text-sm font-medium">{row.label}</p>
                    <p className="text-xs text-muted-foreground">Default {row.default} pts</p>
                  </div>
                  <Input
                    type="number"
                    value={val}
                    onChange={(e) => setEdits((p) => ({ ...p, [row.action]: e.target.value }))}
                    data-testid={`admin-points-input-${row.action}`}
                  />
                  <Button size="sm" onClick={() => save(row)} disabled={pending === undefined || Number(pending) === row.current_points}>
                    <Save className="mr-1 h-4 w-4" /> Save
                  </Button>
                </div>
              );
            })}
          </Card>
        </div>
      ))}
    </div>
  );
}

function AdminPointsRewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [edits, setEdits] = useState({});

  const load = () => {
    setLoading(true);
    pointsAPI.adminGetOptions(true).then((r) => setRewards(Array.isArray(r.data) ? r.data : r.data?.options || [])).catch(() => setRewards([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const seedStandard = async () => {
    setSeeding(true);
    try {
      const r = await pointsAPI.adminSeedRewards();
      toast.success(`Created ${r.data?.created || 0} standard rewards`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not seed");
    } finally { setSeeding(false); }
  };

  const saveReward = async (r) => {
    const patch = edits[r.id];
    if (!patch) return;
    await act(() => pointsAPI.adminUpdateOption(r.id, patch), "Saved", load);
    setEdits((p) => { const { [r.id]: _drop, ...rest } = p; return rest; });
  };

  const toggleActive = async (r) => {
    await act(() => pointsAPI.adminUpdateOption(r.id, { is_active: !r.is_active }), r.is_active ? "Hidden" : "Enabled", load);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">Set the point cost for each reward. Redemptions are manual — you fulfill them from the Redemption Requests tab.</p>
        <Button size="sm" variant="outline" onClick={seedStandard} disabled={seeding}>
          {seeding ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
          Seed standard rewards
        </Button>
      </div>
      {rewards.length === 0 ? (
        <Empty text="No rewards yet. Tap 'Seed standard rewards' to create the six built-in ones." />
      ) : (
        <Card className="divide-y divide-border border-border bg-card">
          {rewards.map((r) => {
            const pending = edits[r.id] || {};
            const cost = pending.points_cost ?? r.points_cost;
            const dirty = pending.points_cost !== undefined || pending.value_description !== undefined;
            return (
              <div key={r.id} className="grid grid-cols-1 gap-2 p-3 md:grid-cols-[1fr_160px_120px_auto] md:items-center" data-testid={`admin-reward-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{r.name}</p>
                  <Input
                    className="mt-1 text-xs"
                    value={pending.value_description ?? r.value_description}
                    onChange={(e) => setEdits((p) => ({ ...p, [r.id]: { ...(p[r.id] || {}), value_description: e.target.value } }))}
                    placeholder="e.g. $25 gift card"
                    data-testid={`admin-reward-value-${r.id}`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Point cost</label>
                  <Input
                    type="number"
                    value={cost}
                    onChange={(e) => setEdits((p) => ({ ...p, [r.id]: { ...(p[r.id] || {}), points_cost: Number(e.target.value) } }))}
                    data-testid={`admin-reward-cost-${r.id}`}
                  />
                </div>
                <Badge variant="secondary" className="w-fit capitalize">{r.category}</Badge>
                <div className="flex gap-2 md:justify-end">
                  <Button size="sm" onClick={() => saveReward(r)} disabled={!dirty}>
                    <Save className="mr-1 h-4 w-4" /> Save
                  </Button>
                  <Button size="sm" variant={r.is_active ? "destructive" : "default"} onClick={() => toggleActive(r)}>
                    {r.is_active ? "Disable" : "Enable"}
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}

function AdminPointsRedemptions() {
  const { data: reqs, loading, reload } = useList(() => pointsAPI.adminRedemptionRequests({ limit: 50 }).then((r) => r.data?.requests || (Array.isArray(r.data) ? r.data : [])));
  const [notes, setNotes] = useState({});
  if (loading) return <Loading />;
  if (!reqs.length) return <Empty text="No redemption requests." />;
  const setNote = (id, v) => setNotes((p) => ({ ...p, [id]: v }));
  return (
    <div className="space-y-2">
      {reqs.map((r) => (
        <Card key={r.id} className="border-border bg-card p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-medium">{r.option_name || r.reward_name || "Redemption"}</p>
              <p className="truncate text-xs text-muted-foreground">
                {r.username} · {r.user_email} · {r.points_cost || r.cost} pts · <span className="capitalize">{r.status}</span>
              </p>
            </div>
            {r.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => act(() => pointsAPI.adminApproveRedemption(r.id, notes[r.id] || ""), "Fulfilled", reload)} data-testid={`admin-redemption-approve-${r.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => act(() => pointsAPI.adminRejectRedemption(r.id, notes[r.id] || ""), "Rejected", reload)} data-testid={`admin-redemption-reject-${r.id}`}><X className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
          {r.status === "pending" && (
            <Input
              className="mt-2 text-xs"
              placeholder="Notes to send to the racer (e.g. discount code SRC-XXXX)"
              value={notes[r.id] || ""}
              onChange={(e) => setNote(r.id, e.target.value)}
              data-testid={`admin-redemption-notes-${r.id}`}
            />
          )}
          {r.admin_notes && <p className="mt-2 text-xs text-muted-foreground">Admin notes: {r.admin_notes}</p>}
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Clubs admin ---------------- */
export function AdminClubs() {
  const { data, loading, reload } = useList(() => achievementsAPI.adminGetClubs().then((r) => (Array.isArray(r.data) ? r.data : r.data?.clubs || [])));
  if (loading) return <Loading />;
  if (!data.length) return <Empty text="No clubs." />;
  return (
    <div className="space-y-2">
      {data.map((c) => (
        <Card key={c.id} className="flex items-center justify-between gap-3 border-border bg-card p-3">
          <div><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.member_count ?? 0} members · {c.potential_members ?? 0} potential</p></div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => act(() => achievementsAPI.adminFeatureClub(c.id, !c.is_featured), "Feature toggled", reload)}>{c.is_featured ? "Unfeature" : "Feature"}</Button>
            <Button size="sm" variant={c.is_active ? "destructive" : "default"} className={c.is_active ? "" : "bg-primary text-primary-foreground"} onClick={() => act(() => achievementsAPI.adminToggleClub(c.id, { is_active: !c.is_active }), "Club toggled", reload)}>{c.is_active ? "Disable" : "Enable"}</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Shipping config ---------------- */
export function AdminShipping() {
  return (
    <div className="space-y-6">
      <ShippingLabelRequests />
      <AdminShippingRateConfig />
      <AdminShippingCategories />
    </div>
  );
}

function AdminShippingRateConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    shippingAdminAPI.getConfig()
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Could not load rate config"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setCfg((prev) => ({ ...(prev || {}), [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await shippingAdminAPI.updateConfig(cfg);
      setCfg(r.data);
      toast.success("Shipping rate config updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save config");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !cfg) return <Loading />;
  const F = (label, key, step = "0.01", hint) => (
    <div>
      <label className="mb-1 block text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <Input type="number" step={step} value={cfg[key] ?? ""} onChange={(e) => set(key, e.target.value)} data-testid={`ship-cfg-${key}`} />
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
  return (
    <Card className="space-y-3 border-border bg-card p-4" data-testid="admin-shipping-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider">Shipping rate config</h3>
          <p className="text-xs text-muted-foreground">Controls the quote formula used across the platform. Applies to every new quote.</p>
        </div>
        <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="ship-cfg-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save</>}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {F("Base rate ($)", "base_rate", "0.01", "Flat fee added to every shipment.")}
        {F("Per pound ($/lb)", "per_pound_rate", "0.01")}
        {F("Per mile ($/mi)", "per_mile_rate", "0.0001")}
        {F("Fuel surcharge (%)", "fuel_surcharge_percent", "0.1")}
        {F("Handling fee ($)", "handling_fee", "0.01")}
        {F("Insurance (%)", "insurance_percent", "0.1", "of declared value")}
        {F("Min shipping cost ($)", "min_shipping_cost", "0.01")}
        {F("Max shipping cost ($)", "max_shipping_cost", "0.01")}
        {F("Dim. weight divisor", "dimensional_weight_divisor", "1", "L × W × H / this = dim weight (lbs). Lower divisor = higher billable weight.")}
      </div>
    </Card>
  );
}

const BLANK_BOX = { length: 0, width: 0, height: 0, weight: 0, supplies_cost: 0 };

function AdminShippingCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState({ category_id: "", category_name: "", small_box: { ...BLANK_BOX }, large_box: { ...BLANK_BOX } });

  const load = () => {
    setLoading(true);
    shippingAdminAPI.listCategories()
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data?.categories || []))
      .catch(() => toast.error("Could not load categories"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createOne = async () => {
    if (!newCat.category_id || !newCat.category_name) { toast.error("Category id and name are required"); return; }
    try {
      await shippingAdminAPI.createCategory(newCat);
      toast.success("Category created");
      setCreating(false);
      setNewCat({ category_id: "", category_name: "", small_box: { ...BLANK_BOX }, large_box: { ...BLANK_BOX } });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-shipping-categories">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider">Shipping categories</h3>
          <p className="text-xs text-muted-foreground">Each category defines the recommended Small &amp; Large box dimensions and supplies cost seen by sellers.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating((v) => !v)} data-testid="ship-cat-add-toggle">
          <Plus className="mr-1 h-4 w-4" /> Add category
        </Button>
      </div>

      {creating && (
        <Card className="space-y-3 border-primary/40 bg-primary/5 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[11px] uppercase text-muted-foreground">Category id (kebab-case)</label>
              <Input value={newCat.category_id} onChange={(e) => setNewCat((v) => ({ ...v, category_id: e.target.value }))} placeholder="steering-wheel" data-testid="new-cat-id" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase text-muted-foreground">Display name</label>
              <Input value={newCat.category_name} onChange={(e) => setNewCat((v) => ({ ...v, category_name: e.target.value }))} placeholder="Steering Wheel" data-testid="new-cat-name" />
            </div>
          </div>
          <BoxEditor label="Small box" value={newCat.small_box} onChange={(b) => setNewCat((v) => ({ ...v, small_box: b }))} idPrefix="new-small" />
          <BoxEditor label="Large box" value={newCat.large_box} onChange={(b) => setNewCat((v) => ({ ...v, large_box: b }))} idPrefix="new-large" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createOne} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-cat-save">Create</Button>
          </div>
        </Card>
      )}

      {loading ? <Loading /> : !rows.length ? <Empty text="No shipping categories configured." /> : (
        <div className="space-y-2">
          {rows.map((c) => <CategoryRow key={c.category_id || c.id} row={c} onDone={load} />)}
        </div>
      )}
    </div>
  );
}

function BoxEditor({ label, value, onChange, idPrefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v === "" ? 0 : Number(v) });
  const F = (k, txt, step = "0.1") => (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{txt}</label>
      <Input type="number" step={step} value={value?.[k] ?? 0} onChange={(e) => set(k, e.target.value)} className="h-9" data-testid={`${idPrefix}-${k}`} />
    </div>
  );
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-primary">{label}</p>
      <div className="grid grid-cols-5 gap-2">
        {F("length", "L (in)")}
        {F("width", "W (in)")}
        {F("height", "H (in)")}
        {F("weight", "Weight (lb)")}
        {F("supplies_cost", "Supplies $", "0.01")}
      </div>
    </div>
  );
}

function CategoryRow({ row, onDone }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);

  const save = async () => {
    try {
      await shippingAdminAPI.updateCategory(row.category_id, {
        category_name: draft.category_name,
        small_box: draft.small_box,
        large_box: draft.large_box,
      });
      toast.success("Category saved");
      setEditing(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const del = async () => {
    if (!confirm(`Delete category "${row.category_name || row.category_id}"?`)) return;
    try {
      await shippingAdminAPI.deleteCategory(row.category_id);
      toast.success("Category deleted");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const box = (b) => b ? `${b.length}×${b.width}×${b.height}" · ${b.weight}lb · $${Number(b.supplies_cost || 0).toFixed(2)}` : "—";

  if (!editing) {
    return (
      <Card className="flex items-start justify-between gap-3 border-border bg-card p-3" data-testid={`ship-cat-row-${row.category_id}`}>
        <div className="min-w-0 flex-1">
          <p className="font-medium">{row.category_name || row.category_id}</p>
          <p className="text-[11px] font-mono text-muted-foreground">/{row.category_id}</p>
          <div className="mt-1 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
            <p><span className="text-muted-foreground">Small:</span> {box(row.small_box)}</p>
            <p><span className="text-muted-foreground">Large:</span> {box(row.large_box)}</p>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`ship-cat-edit-${row.category_id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="destructive" onClick={del} data-testid={`ship-cat-delete-${row.category_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="space-y-3 border-primary/40 bg-primary/5 p-4">
      <div className="flex items-center gap-3">
        <p className="font-mono text-[11px] text-muted-foreground">/{row.category_id}</p>
        <Input value={draft.category_name || ""} onChange={(e) => setDraft((v) => ({ ...v, category_name: e.target.value }))} className="flex-1" data-testid={`ship-cat-name-${row.category_id}`} />
      </div>
      <BoxEditor label="Small box" value={draft.small_box || BLANK_BOX} onChange={(b) => setDraft((v) => ({ ...v, small_box: b }))} idPrefix={`ship-cat-${row.category_id}-small`} />
      <BoxEditor label="Large box" value={draft.large_box || BLANK_BOX} onChange={(b) => setDraft((v) => ({ ...v, large_box: b }))} idPrefix={`ship-cat-${row.category_id}-large`} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => { setEditing(false); setDraft(row); }}>Cancel</Button>
        <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`ship-cat-save-${row.category_id}`}>
          <Save className="mr-1 h-4 w-4" /> Save
        </Button>
      </div>
    </Card>
  );
}

function ShippingLabelRequests() {
  const [payload, setPayload] = useState({ shipping_payments: [], shipping_requests: [], fulfilled_supplies: [] });
  const [loading, setLoading] = useState(true);
  const reload = () => {
    setLoading(true);
    shippingLabelAPI.adminListRequests()
      .then((r) => setPayload(r.data || { shipping_payments: [], shipping_requests: [], fulfilled_supplies: [] }))
      .catch(() => setPayload({ shipping_payments: [], shipping_requests: [], fulfilled_supplies: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);
  const suppliesRows = (payload.supplies_orders || []).map((p) => ({
    kind: "supplies",
    id: p.id,
    shipping_payment_id: p.id,
    seller_id: p.seller_id,
    seller_username: p.seller_username,
    listing_id: p.listing_id,
    listing_title: p.listing_title || "Unknown item",
    total_amount: p.total_amount,
    box_size: p.box_size,
  }));
  const labelRows = [
    ...(payload.shipping_payments || []).map((p) => ({
      kind: "payment",
      id: p.id,
      seller_id: p.seller_id,
      seller_username: p.seller_username,
      listing_id: p.listing_id,
      offer_id: p.offer_id,
      listing_title: p.listing_title || p.item_title || "Unknown item",
      origin_zip: p.origin_zip, destination_zip: p.destination_zip,
      shipping_payment_id: p.id,
    })),
    ...(payload.shipping_requests || []).map((r) => ({
      kind: "request",
      id: r.id,
      seller_id: r.seller_id,
      seller_username: r.seller_username,
      listing_id: r.listing_id,
      listing_title: r.listing_title || "Shipping request",
      shipping_request_id: r.id,
    })),
  ];
  const fulfilledRows = payload.fulfilled_supplies || [];

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider">Supplies to ship <span className="ml-2 text-xs text-muted-foreground">(seller paid, buyer hasn&apos;t bought yet)</span></h3>
          <Button size="sm" variant="outline" onClick={reload}>Refresh</Button>
        </div>
        {loading ? <Loading /> : !suppliesRows.length ? <Empty text="No supplies awaiting shipment." /> : (
          <div className="space-y-2">
            {suppliesRows.map((r) => <SuppliesShipRow key={r.id} row={r} onDone={reload} />)}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider">Shipping labels to send <span className="ml-2 text-xs text-muted-foreground">(buyer paid, awaiting PDF label)</span></h3>
        {loading ? null : !labelRows.length ? <Empty text="No pending shipping label requests." /> : (
          <div className="space-y-2">
            {labelRows.map((r) => <LabelUploadRow key={`${r.kind}-${r.id}`} row={r} onDone={reload} />)}
          </div>
        )}
      </div>

      <FulfilledSuppliesSection rows={fulfilledRows} loading={loading} />
    </div>
  );
}

function FulfilledSuppliesSection({ rows, loading }) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? rows.filter((r) => {
        const q = query.trim().toLowerCase();
        return (
          (r.listing_title || "").toLowerCase().includes(q) ||
          (r.seller_username || "").toLowerCase().includes(q) ||
          (r.supplies_tracking_number || "").toLowerCase().includes(q) ||
          (r.supplies_carrier || "").toLowerCase().includes(q)
        );
      })
    : rows;

  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return String(d); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider">
          Fulfilled supplies <span className="ml-2 text-xs text-muted-foreground">(in transit — keep an eye on tracking)</span>
        </h3>
        <Input
          placeholder="Search title, seller, tracking #, carrier"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-64"
          data-testid="fulfilled-supplies-search"
        />
      </div>
      {loading ? null : !filtered.length ? (
        <Empty text={query ? "No fulfilled shipments match your search." : "No shipments fulfilled yet."} />
      ) : (
        <div className="space-y-2" data-testid="fulfilled-supplies-list">
          {filtered.map((r) => (
            <Card key={r.id} className="border-border bg-card p-3" data-testid={`fulfilled-supplies-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.listing_title || "Unknown item"}</p>
                  <p className="text-xs text-muted-foreground">
                    Seller: {r.seller_username || r.seller_id} · Shipped {fmtDate(r.supplies_shipped_at)}
                  </p>
                  {r.supplies_notes && (
                    <p className="mt-1 text-xs text-muted-foreground">Notes: {r.supplies_notes}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 text-right">
                  <Badge className="border-0 bg-emerald-500/20 text-emerald-400">Shipped</Badge>
                  <p className="text-xs text-muted-foreground">{r.supplies_carrier || "Standard"}</p>
                  <p className="font-mono text-xs" data-testid={`fulfilled-supplies-tracking-${r.id}`}>
                    {r.supplies_tracking_number || "—"}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SuppliesShipRow({ row, onDone }) {
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("Standard");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!tracking.trim()) { toast.error("Tracking number is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("tracking_number", tracking.trim());
      if (carrier) fd.append("carrier", carrier);
      if (notes) fd.append("notes", notes);
      await shippingLabelAPI.adminMarkSuppliesShipped(row.shipping_payment_id, fd);
      toast.success("Supplies marked shipped — seller notified with tracking #");
      setTracking(""); setNotes("");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark shipped");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 border-border bg-card p-3" data-testid={`supplies-row-${row.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium">{row.listing_title}</p>
          <p className="text-xs text-muted-foreground">Seller: {row.seller_username || row.seller_id} · {(row.box_size || "").replace("_", " ")} · ${Number(row.total_amount || 0).toFixed(2)} paid</p>
        </div>
        <Badge className="border-0 bg-amber-500/20 text-amber-400">Supplies to send</Badge>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Tracking # (required)" value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-48" data-testid={`supplies-tracking-${row.id}`} required />
        <Input placeholder="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="w-32" />
        <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="flex-1 min-w-[160px]" />
        <Button className="bg-primary text-primary-foreground" disabled={busy || !tracking.trim()} onClick={submit} data-testid={`supplies-ship-${row.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1 h-4 w-4" /> Mark shipped</>}
        </Button>
      </div>
    </Card>
  );
}

function LabelUploadRow({ row, onDone }) {
  const [file, setFile] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("Standard");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Choose a PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("seller_id", row.seller_id);
      if (row.listing_id) fd.append("listing_id", row.listing_id);
      if (row.offer_id) fd.append("offer_id", row.offer_id);
      if (row.shipping_payment_id) fd.append("shipping_payment_id", row.shipping_payment_id);
      if (row.shipping_request_id) fd.append("shipping_request_id", row.shipping_request_id);
      if (tracking) fd.append("tracking_number", tracking);
      if (carrier) fd.append("carrier", carrier);
      if (notes) fd.append("notes", notes);
      await shippingLabelAPI.adminUploadLabel(fd);
      toast.success("Label sent to seller");
      setFile(null); setTracking(""); setNotes("");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium">{row.listing_title}</p>
          <p className="text-xs text-muted-foreground">Seller: {row.seller_username || row.seller_id} · {row.origin_zip && row.destination_zip ? `${row.origin_zip} → ${row.destination_zip}` : row.kind}</p>
        </div>
        <Badge className="border-0 bg-amber-500/20 text-amber-400">Awaiting label</Badge>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="max-w-xs" data-testid={`label-file-${row.id}`} />
        <Input placeholder="Tracking # (optional)" value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-48" />
        <Input placeholder="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="w-32" />
        <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="flex-1 min-w-[160px]" />
        <Button className="bg-primary text-primary-foreground" disabled={busy || !file} onClick={submit} data-testid={`label-send-${row.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1 h-4 w-4" /> Send label</>}
        </Button>
      </div>
    </Card>
  );
}

/* ---------------- Price compare admin ---------------- */
function BrandCardsManager() {
  const { data, loading, reload } = useList(() => priceCompareAdminAPI.getBrandCards().then((r) => (Array.isArray(r.data) ? r.data : [])));
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [order, setOrder] = useState("");
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file, onDone) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await priceCompareAdminAPI.uploadBrandCardImage(file);
      const url = res.data?.url;
      if (url) { onDone(url); toast.success("Logo uploaded"); }
      else toast.error("Upload failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addCard = () => {
    if (!name.trim() || !imageUrl.trim()) { toast.error("Name and image are required"); return; }
    const payload = { name: name.trim(), image_url: imageUrl.trim(), link_url: linkUrl.trim(), order: Number(order) || 0 };
    act(() => priceCompareAdminAPI.createBrandCard(payload), "Brand card added", reload);
    setName(""); setImageUrl(""); setLinkUrl(""); setOrder("");
  };

  const updateField = (id, field, value) => {
    act(() => priceCompareAdminAPI.updateBrandCard(id, { [field]: value }), "Updated", reload);
  };

  return (
    <div className="space-y-3">
      <Card className="border-border bg-card p-3">
        <h3 className="mb-2 text-sm font-semibold">Landing-page brand carousel</h3>
        <p className="mb-3 text-xs text-muted-foreground">These cards appear in the auto-rotating carousel on the home page. Upload a logo image (PNG, JPG, WEBP, SVG) or paste a hosted image URL.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Brand name" value={name} onChange={(e) => setName(e.target.value)} className="w-40" data-testid="admin-brand-card-name" />
          <Input placeholder="Image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="flex-1 min-w-[200px]" data-testid="admin-brand-card-image" />
          <label className="flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium hover:border-primary/60">
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading..." : "Upload logo"}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadImage(f, setImageUrl); }} data-testid="admin-brand-card-upload" />
          </label>
          <Input placeholder="Link URL (optional)" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="flex-1 min-w-[160px]" data-testid="admin-brand-card-link" />
          <Input placeholder="Order" value={order} onChange={(e) => setOrder(e.target.value)} className="w-20" data-testid="admin-brand-card-order" />
          <Button className="bg-primary text-primary-foreground" onClick={addCard} data-testid="admin-brand-card-add">Add card</Button>
        </div>
      </Card>
      {loading ? <Loading /> : !data.length ? <Empty text="No brand cards yet." /> : (
        <div className="space-y-2">
          {data.map((c) => (
            <Card key={c.id} className="flex items-center gap-3 border-border bg-card p-3" data-testid={`admin-brand-card-${c.id}`}>
              <img src={c.image_url} alt={c.name} className="h-12 w-12 flex-shrink-0 rounded object-contain bg-white/5" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{c.name}</p>
                <p className="truncate text-xs text-muted-foreground">{c.image_url}</p>
                {c.link_url && <p className="truncate text-xs text-muted-foreground">→ {c.link_url}</p>}
              </div>
              <label className="flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary/60">
                <Upload className="h-3.5 w-3.5" /> Replace
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadImage(f, (url) => updateField(c.id, "image_url", url)); }} />
              </label>
              <Input type="number" defaultValue={c.order} onBlur={(e) => { const n = Number(e.target.value); if (n !== c.order) updateField(c.id, "order", n); }} className="w-20" />
              <Button size="sm" variant="outline" onClick={() => updateField(c.id, "is_active", !c.is_active)}>{c.is_active ? "Hide" : "Show"}</Button>
              <Button size="sm" variant="destructive" onClick={() => act(() => priceCompareAdminAPI.deleteBrandCard(c.id), "Deleted", reload)}><Trash2 className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------------- Price Compare: retailer visibility ---------------- */
function RetailerVisibilityManager() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    priceCompareAdminAPI.getRetailerVisibility()
      .then((r) => setRetailers(r.data?.retailers || []))
      .catch(() => setRetailers([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (rt) => {
    try {
      await priceCompareAdminAPI.setRetailerVisibility(rt.id, !rt.hidden);
      setRetailers((prev) => prev.map((x) => (x.id === rt.id ? { ...x, hidden: !x.hidden } : x)));
      toast.success(!rt.hidden ? `${rt.name} hidden from results` : `${rt.name} now visible`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update retailer");
    }
  };

  return (
    <Card className="space-y-3 border-border bg-card p-4" data-testid="admin-pc-retailer-visibility">
      <div>
        <p className="font-semibold uppercase tracking-wider">Retailer visibility</p>
        <p className="text-xs text-muted-foreground">
          Retailers detected from uploads (column 9). Hidden retailers are excluded from search results.
        </p>
      </div>
      {loading ? <Loading /> : !retailers.length ? (
        <Empty text="No retailers yet — upload a spreadsheet to populate this list." />
      ) : (
        <div className="space-y-2">
          {retailers.map((rt) => (
            <div key={rt.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2" data-testid={`admin-pc-retailer-${rt.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{rt.name}</p>
                <p className="text-xs text-muted-foreground">{rt.product_count} product{rt.product_count === 1 ? "" : "s"}</p>
              </div>
              <button
                type="button"
                onClick={() => toggle(rt)}
                data-testid={`admin-pc-retailer-toggle-${rt.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${rt.hidden ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-primary"}`}
              >
                {rt.hidden ? <><ShieldOff className="h-3.5 w-3.5" /> Hidden</> : <><ShieldCheck className="h-3.5 w-3.5" /> Visible</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ---------------- Price Compare: currency settings ---------------- */
function CurrencySettingsManager() {
  const [settings, setSettings] = useState(null);
  const [modifier, setModifier] = useState("1");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    priceCompareAdminAPI.getCurrencySettings()
      .then((r) => { setSettings(r.data); setModifier(String(r.data?.rate_modifier ?? 1)); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const val = parseFloat(modifier);
    if (!val || val <= 0) { toast.error("Modifier must be greater than 0"); return; }
    setSaving(true);
    try {
      const r = await priceCompareAdminAPI.setCurrencyModifier(val);
      setSettings(r.data);
      setModifier(String(r.data?.rate_modifier ?? val));
      toast.success("Currency modifier saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await priceCompareAdminAPI.refreshCurrencyRate();
      setSettings(r.data);
      toast.success("Live rate refreshed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  return (
    <Card className="space-y-3 border-border bg-card p-4" data-testid="admin-pc-currency">
      <div>
        <p className="font-semibold uppercase tracking-wider">Currency conversion (USD ↔ CAD)</p>
        <p className="text-xs text-muted-foreground">
          Shoppers can view all prices in USD or CAD. Prices convert using the live rate × your modifier
          (1 = exact live rate, 1.01 = +1%, 0.99 = −1%).
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="rounded-md border border-border bg-background p-2">
          <p className="text-[10px] uppercase text-muted-foreground">Live USD→CAD</p>
          <p className="font-display font-bold">{settings ? settings.usd_to_cad_rate : "—"}</p>
        </div>
        <div className="rounded-md border border-border bg-background p-2">
          <p className="text-[10px] uppercase text-muted-foreground">Modifier</p>
          <p className="font-display font-bold">{settings ? settings.rate_modifier : "—"}</p>
        </div>
        <div className="rounded-md border border-primary/40 bg-primary/10 p-2">
          <p className="text-[10px] uppercase text-primary">Effective rate</p>
          <p className="font-display font-bold text-primary">{settings ? settings.effective_rate : "—"}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="number"
          step="0.01"
          min="0.01"
          value={modifier}
          onChange={(e) => setModifier(e.target.value)}
          className="w-32"
          data-testid="admin-pc-currency-modifier"
        />
        <Button className="bg-primary text-primary-foreground" onClick={save} disabled={saving} data-testid="admin-pc-currency-save">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save modifier
        </Button>
        <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="admin-pc-currency-refresh">
          {refreshing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Refresh live rate
        </Button>
        {settings?.rate_updated_at && (
          <span className="text-xs text-muted-foreground">Updated: {new Date(settings.rate_updated_at).toLocaleString()}</span>
        )}
      </div>
    </Card>
  );
}

export function AdminPriceCompare() {
  const { data, loading, reload } = useList(() => priceCompareAdminAPI.getRetailers().then((r) => (Array.isArray(r.data) ? r.data : r.data?.retailers || [])));
  const [name, setName] = useState(""); const [url, setUrl] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, retailers: [], last_upload_at: null });
  const [replaceExisting, setReplaceExisting] = useState(false);

  const loadSummary = () => priceCompareAdminAPI.priceCompareProductsSummary().then((r) => setSummary(r.data || { total: 0, retailers: [], last_upload_at: null })).catch(() => {});
  useEffect(() => { loadSummary(); }, []);

  const addRetailer = () => {
    if (!name || !url) return;
    act(() => priceCompareAdminAPI.createRetailer({ name, website_url: url }), "Retailer added", reload);
    setName(""); setUrl("");
  };
  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await priceCompareAPI.adminRefreshPrices();
      const d = res.data || {};
      toast.success(`Refreshed ${d.queries_refreshed || 0} queries · ${d.snapshots_written || 0} snapshots · ${d.alerts_sent || 0} alerts sent`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };
  const uploadProducts = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await priceCompareAdminAPI.uploadPriceCompareProducts(file, replaceExisting);
      const d = res.data || {};
      const parts = [`${d.inserted || 0} added`, `${d.updated || 0} updated`];
      if (d.price_changes) parts.push(`${d.price_changes} price change${d.price_changes === 1 ? "" : "s"}`);
      if (d.alerts_sent) parts.push(`${d.alerts_sent} watcher alert${d.alerts_sent === 1 ? "" : "s"}`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      toast.success(`Upload complete: ${parts.join(", ")}.`);
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const wipe = () => {
    if (!window.confirm("Delete every product in the Price Compare database?")) return;
    act(() => priceCompareAdminAPI.clearPriceCompareProducts(), "Price Compare products cleared", loadSummary);
  };

  return (
    <div className="space-y-6">
      <PriceCompareAnalytics />
      <Card className="space-y-3 border-border bg-card p-4" data-testid="admin-pc-upload-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-semibold uppercase tracking-wider">Price Compare product database</p>
            <p className="text-xs text-muted-foreground">
              Upload the spreadsheet(s) that power the Price Compare search. This is separate from the general Product Catalog (brands/models used by forum posts, profiles, and listings).
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              16 columns: URL, image URL, name, brand, price, currency, availability, shipping, retailer, category, region (US/CA/ALL), keyword 1-5.
            </p>
          </div>
          <Badge className="border-0 bg-primary/20 text-primary">
            {summary.total.toLocaleString()} products · {summary.retailers.length} retailers
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90" data-testid="admin-pc-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload spreadsheet"}
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" disabled={uploading} onChange={uploadProducts} data-testid="admin-pc-upload-input" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} data-testid="admin-pc-replace-checkbox" />
            Replace existing products
          </label>
          <Button variant="destructive" size="sm" onClick={wipe} data-testid="admin-pc-wipe">
            <Trash2 className="mr-1 h-4 w-4" /> Wipe database
          </Button>
          <span className="ml-auto text-xs text-muted-foreground">
            {summary.last_upload_at ? `Last upload: ${new Date(summary.last_upload_at).toLocaleString()}` : "No uploads yet."}
          </span>
        </div>
        <details className="rounded-md border border-border bg-secondary/40 p-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium text-foreground">Expected columns</summary>
          <ol className="mt-2 list-inside list-decimal space-y-0.5">
            <li>Product URL</li>
            <li>Image URL (used as display image)</li>
            <li>Product name (indexed for search)</li>
            <li>Brand / manufacturer (indexed for search)</li>
            <li>Price (numeric)</li>
            <li>Currency (e.g. USD, CAD, EUR)</li>
            <li>Availability (In Stock / Out of Stock)</li>
            <li>Shipping (Free / Calculated at checkout / etc.)</li>
            <li>Retailer (e.g. Amazon, Fanatec, Pit Lane Sim Racing)</li>
            <li>Category (used for filtering/sorting, e.g. Wheelbase, Pedals)</li>
            <li>Keyword 1 (optional — product is found when searched by this word)</li>
            <li>Keyword 2 (optional)</li>
            <li>Keyword 3 (optional)</li>
            <li>Keyword 4 (optional)</li>
            <li>Keyword 5 (optional)</li>
          </ol>
          <p className="mt-2">The uploader is idempotent: existing rows (matched by Product URL) are updated in place.</p>
        </details>
      </Card>

      <RetailerVisibilityManager />
      <CurrencySettingsManager />

      <Card className="flex items-center justify-between gap-3 border-border bg-card p-4">
        <div>
          <p className="font-semibold uppercase tracking-wider">Daily price refresh</p>
          <p className="text-xs text-muted-foreground">Snapshots all watched products and notifies users on price changes.</p>
        </div>
        <Button className="bg-primary text-primary-foreground" disabled={refreshing} onClick={doRefresh} data-testid="admin-refresh-prices">
          {refreshing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          Run refresh now
        </Button>
      </Card>
      <BrandCardsManager />
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Retailers</h3>
        <Card className="flex flex-wrap gap-2 border-border bg-card p-3">
          <Input placeholder="Retailer name" value={name} onChange={(e) => setName(e.target.value)} className="w-40" />
          <Input placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} className="flex-1" />
          <Button className="bg-primary text-primary-foreground" onClick={addRetailer}>Add retailer</Button>
        </Card>
        {loading ? <Loading /> : !data.length ? <Empty text="No retailers." /> : (
          <div className="space-y-2">
            {data.map((r) => (
              <Card key={r.id} className="flex items-center justify-between gap-3 border-border bg-card p-3">
                <div><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.website_url}</p></div>
                <Button size="sm" variant="destructive" onClick={() => act(() => priceCompareAdminAPI.deleteRetailer(r.id), "Deleted", reload)}><Trash2 className="h-4 w-4" /></Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Users ---------------- */
export function AdminUsers() {
  const [search, setSearch] = useState("");
  const [editingSlug, setEditingSlug] = useState(null);
  const [slugValue, setSlugValue] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [catValue, setCatValue] = useState("");
  const [catDetails, setCatDetails] = useState("");
  const [founding, setFounding] = useState(null);
  const [announcer, setAnnouncer] = useState("");
  const [editingPoints, setEditingPoints] = useState(null);
  const [pointsValue, setPointsValue] = useState("");
  const [pointsReason, setPointsReason] = useState("");
  const { data, loading, reload } = useList(() => adminAPI.getUsers({ limit: 50 }).then((r) => r.data?.users || (Array.isArray(r.data) ? r.data : [])));
  const filtered = data.filter((u) => !search || (u.username || "").toLowerCase().includes(search.toLowerCase()) || (u.email || "").toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    adminAPI.getSettings().then((r) => {
      setFounding(!!r.data?.founding_member_enabled);
      setAnnouncer(r.data?.announcer_account_id || "");
    }).catch(() => {});
  }, []);

  const changeAnnouncer = async (val) => {
    const next = val === "__super__" ? "" : val;
    setAnnouncer(next);
    try {
      await adminAPI.updateSettings({ announcer_account_id: next });
      toast.success("Announcer account updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const toggleFounding = async (next) => {
    setFounding(next);
    try {
      await adminAPI.updateSettings({ founding_member_enabled: next });
      toast.success(next ? "Founding Member signups enabled" : "Founding Member signups disabled");
    } catch (e) {
      setFounding(!next);
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const startEditSlug = (u) => {
    setEditingSlug(u.id);
    setSlugValue(u.profile_slug || "");
  };

  const saveSlug = async (u) => {
    const cleaned = slugValue.trim().toLowerCase();
    if (!cleaned) { toast.error("Enter a slug"); return; }
    try {
      await adminAPI.updateUserSlug(u.id, cleaned);
      toast.success("Profile URL updated");
      setEditingSlug(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update slug");
    }
  };

  const startEditCat = (u) => {
    setEditingCat(u.id);
    setCatValue(u.member_category || "");
    setCatDetails(u.member_category_details || "");
  };

  const saveCat = async (u) => {
    const needsDetails = catValue === "sim_racing_expert" || catValue === "content_creator";
    try {
      await adminAPI.setUserCategory(u.id, {
        member_category: catValue,
        member_category_details: needsDetails ? catDetails : "",
      });
      toast.success("Membership tag updated");
      setEditingCat(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update tag");
    }
  };

  if (loading) return <Loading />;
  const startEditPoints = (u) => { setEditingPoints(u.id); setPointsValue(""); setPointsReason(""); };
  const savePoints = async (u) => {
    const delta = parseInt(pointsValue, 10);
    if (!delta || Number.isNaN(delta)) { toast.error("Enter a non-zero amount (use a minus sign to remove)"); return; }
    try {
      await pointsAPI.adminAdjust(u.id, { points: delta, reason: pointsReason || "Admin adjustment" });
      toast.success(`${delta > 0 ? "Added" : "Removed"} ${Math.abs(delta)} points`);
      setEditingPoints(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not adjust points");
    }
  };
  return (
    <div className="space-y-3">
      <Card className="flex items-center justify-between gap-3 border-border bg-card p-3">
        <div className="min-w-0">
          <p className="font-medium">Founding Member signups</p>
          <p className="text-xs text-muted-foreground">When on, new members must pick Founding Member, Sim Racing Expert, or Content Creator. Turn off after launch to close Founding Member.</p>
        </div>
        <Switch checked={!!founding} onCheckedChange={toggleFounding} disabled={founding === null} data-testid="admin-founding-toggle" />
      </Card>
      <Card className="space-y-2 border-border bg-card p-3">
        <div>
          <p className="font-medium">Announcer account</p>
          <p className="text-xs text-muted-foreground">Account that owns announcement messages in members&apos; inboxes, so it receives and can reply to responses. Everything else goes out as the brand. Defaults to the owner (super admin).</p>
        </div>
        <Select value={announcer || "__super__"} onValueChange={changeAnnouncer}>
          <SelectTrigger className="h-9 text-sm" data-testid="admin-announcer-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__super__">Owner (super admin) — default</SelectItem>
            {data.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.username}{u.email ? ` (${u.email})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>
      <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-users-search" />
      <div className="space-y-2">
        {filtered.map((u) => (
          <Card key={u.id} className="space-y-2 border-border bg-card p-3" data-testid={`admin-user-${u.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-medium">
                  {u.username} {u.is_super_admin ? <Badge className="ml-1 border-0 bg-accent/20 text-accent">Super</Badge> : u.is_admin ? <Badge className="ml-1 border-0 bg-primary/20 text-primary">Admin</Badge> : null}
                  {u.member_category && memberCategoryLabel(u.member_category) && (
                    <Badge className="ml-1 border-0 bg-secondary text-foreground/90" data-testid={`admin-user-cat-${u.id}`}>{memberCategoryLabel(u.member_category)}</Badge>
                  )}
                  {u.is_founding_member && u.member_category !== "founding_member" && (
                    <Badge className="ml-1 border-0 bg-amber-400/20 text-amber-300">Founding</Badge>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">{u.email} · {u.points ?? 0} pts</p>
                {u.profile_slug && (
                  <p className="truncate text-[11px] text-primary/80 font-mono">/u/{u.profile_slug}</p>
                )}
                {u.member_category_details && (
                  <p className="mt-1 whitespace-pre-wrap text-[11px] text-muted-foreground"><span className="font-semibold">Details:</span> {u.member_category_details}</p>
                )}
              </div>
              <div className="flex flex-shrink-0 gap-2">
                <Button size="sm" variant="outline" onClick={() => startEditCat(u)} data-testid={`admin-user-cat-edit-${u.id}`}>
                  Tag
                </Button>
                <Button size="sm" variant="outline" onClick={() => startEditSlug(u)} data-testid={`admin-user-slug-edit-${u.id}`}>
                  URL
                </Button>
                <Button size="sm" variant="outline" onClick={() => startEditPoints(u)} data-testid={`admin-user-points-edit-${u.id}`}>
                  Points
                </Button>
                <Button size="sm" variant="outline" onClick={() => act(() => adminAPI.setRole(u.id, u.is_admin ? "user" : "admin"), "Role updated", reload)}>
                  {u.is_admin ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" title={u.is_banned ? "Unban user" : "Ban user"} onClick={() => act(() => adminAPI.banUser(u.id, !u.is_banned), u.is_banned ? "Unbanned" : "Banned", reload)}><Ban className="h-4 w-4" /></Button>
                {!u.is_admin && !u.is_super_admin && (
                  <Button
                    size="sm"
                    variant="destructive"
                    title="Permanently delete (remove) this user"
                    data-testid={`admin-delete-user-${u.id}`}
                    onClick={() => {
                      if (window.confirm(`Permanently DELETE ${u.username}? This removes their account and social content, and frees their email (${u.email}) and username for reuse. This cannot be undone.`)) {
                        act(() => adminAPI.deleteUser(u.id), "User deleted", reload);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {editingPoints === u.id && (
              <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-2" data-testid={`admin-user-points-panel-${u.id}`}>
                <p className="text-xs text-muted-foreground">Current balance: <span className="font-semibold text-foreground">{u.points ?? 0}</span> pts. Enter a positive number to add or a negative number to remove.</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={pointsValue}
                    onChange={(e) => setPointsValue(e.target.value)}
                    placeholder="e.g. 50 or -25"
                    className="h-8 w-28 text-sm"
                    data-testid={`admin-user-points-input-${u.id}`}
                  />
                  <Input
                    value={pointsReason}
                    onChange={(e) => setPointsReason(e.target.value)}
                    placeholder="Reason (optional)"
                    className="h-8 flex-1 text-sm"
                    data-testid={`admin-user-points-reason-${u.id}`}
                  />
                  <Button size="sm" onClick={() => savePoints(u)} data-testid={`admin-user-points-save-${u.id}`}>Apply</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingPoints(null)}>Cancel</Button>
                </div>
              </div>
            )}
            {editingSlug === u.id && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/30 p-2">
                <span className="text-xs font-mono text-muted-foreground">/u/</span>
                <Input
                  value={slugValue}
                  onChange={(e) => setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 30))}
                  placeholder="custom-url"
                  className="h-8 text-sm"
                  data-testid={`admin-user-slug-input-${u.id}`}
                />
                <Button size="sm" onClick={() => saveSlug(u)} data-testid={`admin-user-slug-save-${u.id}`}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingSlug(null)}>Cancel</Button>
              </div>
            )}
            {editingCat === u.id && (
              <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-2">
                <Select value={catValue || "none"} onValueChange={(v) => setCatValue(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid={`admin-user-cat-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No tag (regular)</SelectItem>
                    <SelectItem value="founding_member">Founding Member</SelectItem>
                    <SelectItem value="sim_racing_expert">Sim Racing Expert</SelectItem>
                    <SelectItem value="content_creator">Content Creator</SelectItem>
                  </SelectContent>
                </Select>
                {(catValue === "sim_racing_expert" || catValue === "content_creator") && (
                  <Textarea
                    value={catDetails}
                    onChange={(e) => setCatDetails(e.target.value)}
                    rows={2}
                    placeholder="Short bio and/or online profiles"
                    className="text-sm"
                    data-testid={`admin-user-cat-details-${u.id}`}
                  />
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveCat(u)} data-testid={`admin-user-cat-save-${u.id}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingCat(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Product Catalog ---------------- */
export function AdminCatalog() {
  const { data, loading, reload } = useList(() => catalogAPI.getProducts().then((r) => r.data?.products || []));
  const [mfr, setMfr] = useState("");
  const [prod, setProd] = useState("");
  const [cat, setCat] = useState("");
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await catalogAPI.uploadCSV(file);
      const { added, skipped, total } = res.data;
      toast.success(`Import complete: ${added} added, ${skipped} skipped (${total} total)`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addProduct = () => {
    if (!mfr.trim() || !prod.trim()) { toast.error("Enter manufacturer and product"); return; }
    act(() => catalogAPI.addProduct({ manufacturer: mfr.trim(), product_name: prod.trim(), category: cat.trim() }), "Product added", reload);
    setMfr(""); setProd(""); setCat("");
  };

  const exportCSV = async () => {
    try {
      const res = await catalogAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "product_catalog.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border bg-card p-4">
        <h3 className="mb-1 font-semibold">Import from CSV</h3>
        <p className="mb-3 text-xs text-muted-foreground">3 columns with a header row: <code className="text-primary">manufacturer,product_name,category</code>. Category is optional (legacy 2-column files still import). New rows are merged and de-duplicated.</p>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90" data-testid="catalog-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={onUpload} data-testid="catalog-upload-input" />
          </label>
          <Button variant="outline" onClick={exportCSV} data-testid="catalog-export-button"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
        </div>
      </Card>

      <Card className="flex flex-wrap gap-2 border-border bg-card p-3">
        <Input placeholder="Manufacturer" value={mfr} onChange={(e) => setMfr(e.target.value)} className="w-44" data-testid="catalog-add-manufacturer" />
        <Input placeholder="Product name" value={prod} onChange={(e) => setProd(e.target.value)} className="flex-1" data-testid="catalog-add-product" />
        <Input placeholder="Category (optional)" value={cat} onChange={(e) => setCat(e.target.value)} className="w-44" data-testid="catalog-add-category" />
        <Button className="bg-primary text-primary-foreground" onClick={addProduct} data-testid="catalog-add-button">Add product</Button>
      </Card>

      {loading ? <Loading /> : !data.length ? <Empty text="Catalog is empty — upload a CSV to get started." /> : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{data.length} products</p>
          {data.map((p) => (
            <Card key={p.id} className="flex items-center justify-between gap-3 border-border bg-card p-3" data-testid={`catalog-row-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{p.product_name}</span>
                <span className="text-xs text-muted-foreground">{p.manufacturer}</span>
                {p.category ? <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{p.category}</span> : null}
              </div>
              <Button size="sm" variant="destructive" onClick={() => act(() => catalogAPI.deleteProduct(p.id), "Deleted", reload)} data-testid={`catalog-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Approvals ---------------- */
export function AdminApprovals() {
  const { data, loading, reload } = useList(() => adminAPI.getPendingAccounts().then((r) => r.data?.pending_accounts || []));
  if (loading) return <Loading />;
  if (!data.length) return <Empty text="No accounts pending approval." />;
  return (
    <div className="space-y-2">
      {data.map((u) => (
        <Card key={u.id} className="flex items-center justify-between gap-3 border-border bg-card p-3" data-testid={`admin-approval-${u.id}`}>
          <div className="min-w-0">
            <p className="font-medium">
              {u.username}
              {u.member_category && memberCategoryLabel(u.member_category) && (
                <Badge className="ml-2 border-0 bg-secondary text-foreground/90">{memberCategoryLabel(u.member_category)}</Badge>
              )}
              {u.is_founding_member && u.member_category !== "founding_member" && (
                <Badge className="ml-2 border-0 bg-amber-400/20 text-amber-300">Founding</Badge>
              )}
            </p>
            <p className="text-xs text-muted-foreground">{u.email} · {new Date(u.created_at).toLocaleDateString()}</p>
            {u.member_category_details && (
              <p className="mt-1 whitespace-pre-wrap text-[11px] text-muted-foreground"><span className="font-semibold">Details:</span> {u.member_category_details}</p>
            )}
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => act(() => adminAPI.approveAccount(u.id, "approve"), "Approved", reload)} data-testid={`admin-approve-${u.id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>
            <Button size="sm" variant="destructive" onClick={() => act(() => adminAPI.approveAccount(u.id, "reject"), "Rejected", reload)} data-testid={`admin-reject-${u.id}`}><X className="mr-1 h-4 w-4" /> Reject</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}


/* ---------------- SimHub Track Aliases ---------------- */
export function AdminSimHubTracks() {
  const { data: tracks, loading, reload } = useList(() => adminSimhubAPI.listTracks().then(r => r.data));
  const [query, setQuery] = useState("");
  const [edits, setEdits] = useState({});

  const setEdit = (key, patch) => setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const filtered = tracks.filter((t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      t.track_key.includes(q) ||
      (t.display_name || "").toLowerCase().includes(q) ||
      (t.raw_track_names || []).some((n) => (n || "").toLowerCase().includes(q))
    );
  });

  const saveRow = async (t) => {
    const patch = edits[t.track_key];
    if (!patch) return;
    await act(() => adminSimhubAPI.updateTrack(t.track_key, patch), "Track updated", reload);
    setEdits((prev) => { const { [t.track_key]: _drop, ...rest } = prev; return rest; });
  };

  const togglePublic = async (t) => {
    await act(
      () => adminSimhubAPI.updateTrack(t.track_key, {
        is_public: !t.is_public,
        display_name: (edits[t.track_key]?.display_name ?? t.display_name) || t.track_key,
      }),
      t.is_public ? "Hidden" : "Published",
      reload,
    );
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">SimHub Track Aliases</h3>
          <p className="text-xs text-muted-foreground">Rename raw telemetry track IDs and pick which appear on public leaderboards.</p>
        </div>
        <Input placeholder="Search tracks…" value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-xs" data-testid="admin-simhub-tracks-search" />
      </div>

      {filtered.length === 0 ? (
        <Empty text={tracks.length === 0 ? "No SimHub sessions have been recorded yet." : "No tracks match your search."} />
      ) : (
        <Card className="divide-y divide-border border-border bg-card">
          {filtered.map((t) => {
            const pending = edits[t.track_key] || {};
            const displayName = pending.display_name ?? t.display_name;
            const sortOrder = pending.sort_order ?? t.sort_order ?? 0;
            const isPublic = pending.is_public ?? t.is_public;
            const dirty = pending.display_name !== undefined || pending.sort_order !== undefined || pending.is_public !== undefined;
            return (
              <div key={t.track_key} className="grid grid-cols-1 gap-3 p-3 md:grid-cols-[1fr_2fr_auto] md:items-center" data-testid={`admin-simhub-track-${t.track_key}`}>
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs text-muted-foreground">{t.track_key}</p>
                  <p className="truncate text-xs text-muted-foreground">{(t.raw_track_names || []).join(" · ")}</p>
                  <p className="mt-1 text-xs">{t.racers} racers · {t.sessions} sessions · {t.laps} laps</p>
                </div>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <div className="flex-1">
                    <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Display name</label>
                    <Input
                      placeholder="e.g. Spa-Francorchamps"
                      value={displayName}
                      onChange={(e) => setEdit(t.track_key, { display_name: e.target.value })}
                      data-testid={`admin-simhub-track-display-${t.track_key}`}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-muted-foreground" title="Lower number appears first on the leaderboards list">List position</label>
                    <Input
                      type="number"
                      className="w-20"
                      placeholder="0"
                      value={sortOrder}
                      onChange={(e) => setEdit(t.track_key, { sort_order: Number(e.target.value) || 0 })}
                      data-testid={`admin-simhub-track-order-${t.track_key}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-2 py-1">
                    <Switch
                      checked={!!isPublic}
                      onCheckedChange={(v) => setEdit(t.track_key, { is_public: !!v, display_name: displayName || t.track_key })}
                      data-testid={`admin-simhub-track-public-${t.track_key}`}
                    />
                    <span className="text-xs font-medium">Public</span>
                  </div>
                </div>
                <div className="flex gap-2 md:justify-end">
                  <Button
                    size="sm"
                    onClick={() => saveRow(t)}
                    disabled={!dirty || (isPublic && !((displayName || "").trim()))}
                    data-testid={`admin-simhub-track-save-${t.track_key}`}
                  >
                    <Save className="mr-1 h-4 w-4" /> Save
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}


/* ---------------- SimHub Analytics ---------------- */
function formatLapTime(ms) {
  if (!ms || ms <= 0) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
}

export function AdminSimHubAnalytics() {
  const [tab, setTab] = useState("cars");
  const [cars, setCars] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminSimhubAPI.analyticsCars().then((r) => r.data).catch(() => []),
      adminSimhubAPI.analyticsTracks().then((r) => r.data).catch(() => []),
    ]).then(([c, t]) => {
      setCars(c || []);
      setTracks(t || []);
      setLoading(false);
    });
  }, []);

  const openMembers = async (row) => {
    setSelected({ ...row, kind: tab });
    setLoadingMembers(true);
    setMembers([]);
    try {
      const res = tab === "cars"
        ? await adminSimhubAPI.carMembers(row.car_key)
        : await adminSimhubAPI.trackMembers(row.track_key);
      setMembers(res.data || []);
    } catch { setMembers([]); }
    finally { setLoadingMembers(false); }
  };

  const rows = tab === "cars" ? cars : tracks;
  const filtered = rows.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return tab === "cars"
      ? (r.car_model || "").toLowerCase().includes(q) || (r.car_class || "").toLowerCase().includes(q)
      : (r.display_name || r.raw_track_name || r.track_key || "").toLowerCase().includes(q);
  });

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">SimHub Community Analytics</h3>
          <p className="text-xs text-muted-foreground">See which cars and tracks members race. Tap a row to see the racers driving them.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border bg-secondary/40 p-0.5 text-xs">
            <button className={`rounded px-3 py-1 ${tab === "cars" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => { setTab("cars"); setSelected(null); setMembers([]); }} data-testid="admin-simhub-analytics-cars">Cars</button>
            <button className={`rounded px-3 py-1 ${tab === "tracks" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => { setTab("tracks"); setSelected(null); setMembers([]); }} data-testid="admin-simhub-analytics-tracks">Tracks</button>
          </div>
          <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-xs" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <Card className="border-border bg-card">
          {filtered.length === 0 ? (
            <Empty text="No data yet." />
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((r) => (
                <button
                  key={tab === "cars" ? r.car_key : r.track_key}
                  onClick={() => openMembers(r)}
                  className={`grid w-full grid-cols-[1fr_auto] items-center gap-2 p-3 text-left transition-colors hover:bg-secondary/40 ${
                    (selected && (tab === "cars" ? selected.car_key === r.car_key : selected.track_key === r.track_key)) ? "bg-secondary/60" : ""
                  }`}
                  data-testid={`admin-simhub-row-${tab}-${(tab === "cars" ? r.car_key : r.track_key)}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {tab === "cars" ? (r.car_model || r.car_key) : (r.display_name || r.raw_track_name || r.track_key)}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {tab === "cars" ? (r.car_class || "unclassified") : `${r.track_key}${r.is_public ? " · public" : ""}`}
                    </p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-bold text-primary">{r.racers} racers</p>
                    <p className="text-muted-foreground">{r.sessions} sess · {r.laps} laps</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="border-border bg-card">
          {!selected ? (
            <Empty text="Select a row to view members." />
          ) : loadingMembers ? (
            <Loading />
          ) : members.length === 0 ? (
            <Empty text="No members found." />
          ) : (
            <div className="divide-y divide-border">
              <div className="p-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {selected.kind === "cars"
                  ? `Racers driving ${selected.car_model || selected.car_key}`
                  : `Racers on ${selected.display_name || selected.raw_track_name || selected.track_key}`}
              </div>
              {members.map((m) => (
                <div key={m.user_id} className="grid grid-cols-[1fr_auto] items-center gap-2 p-3" data-testid={`admin-simhub-member-${m.user_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{m.username}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {m.sessions} sess · {m.laps} laps
                      {selected.kind === "tracks" && m.best_lap_ms ? ` · best ${formatLapTime(m.best_lap_ms)}` : ""}
                    </p>
                  </div>
                  <p className="text-right text-xs text-muted-foreground">{m.last_used ? new Date(m.last_used).toLocaleDateString() : ""}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


/* ---------------- SimHub Club Suggestions ---------------- */
export function AdminSimHubClubSuggestions() {
  const [data, setData] = useState({ cars: [], tracks: [], gear: [] });
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cars");
  const [pending, setPending] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminSimhubAPI.clubSuggestions({ min_racers: 2 }).then((r) => r.data).catch(() => ({ cars: [], tracks: [], gear: [] })),
      adminSimhubAPI.listCustomClubs().then((r) => r.data).catch(() => []),
    ]).then(([s, c]) => {
      setData(s);
      setExisting(c || []);
      setLoading(false);
    });
  };
  useEffect(() => load(), []);

  const createClub = async (row) => {
    setPending(`${row.kind}:${row.key}`);
    try {
      await adminSimhubAPI.createClub({ kind: row.kind, key: row.key });
      toast.success(`Created "${row.display_name}" club`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create club");
    } finally {
      setPending(null);
    }
  };

  if (loading) return <Loading />;
  const rows = tab === "cars" ? data.cars : tab === "tracks" ? data.tracks : (data.gear || []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Suggested Clubs</h3>
          <p className="text-xs text-muted-foreground">Cars, tracks and gear brands shared by multiple members. Create a club with one click to pool those members.</p>
        </div>
        <div className="flex rounded-md border border-border bg-secondary/40 p-0.5 text-xs">
          <button className={`rounded px-3 py-1 ${tab === "cars" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => setTab("cars")} data-testid="admin-club-tab-cars">Cars ({data.cars?.length || 0})</button>
          <button className={`rounded px-3 py-1 ${tab === "tracks" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => setTab("tracks")} data-testid="admin-club-tab-tracks">Tracks ({data.tracks?.length || 0})</button>
          <button className={`rounded px-3 py-1 ${tab === "gear" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} onClick={() => setTab("gear")} data-testid="admin-club-tab-gear">Gear ({data.gear?.length || 0})</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty text={`No ${tab} shared by multiple racers yet.`} />
      ) : (
        <Card className="divide-y divide-border border-border bg-card">
          {rows.map((r) => (
            <div key={`${r.kind}-${r.key}`} className="grid grid-cols-[1fr_auto] items-center gap-3 p-3" data-testid={`admin-club-row-${r.kind}-${r.key}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{r.display_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {r.racers} racers · {r.sessions} sessions · {r.laps} laps
                  {r.kind === "car" && r.car_class ? ` · ${r.car_class}` : ""}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => createClub(r)}
                disabled={r.has_club || pending === `${r.kind}:${r.key}`}
                data-testid={`admin-create-club-${r.kind}-${r.key}`}
              >
                {pending === `${r.kind}:${r.key}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                {r.has_club ? "Already a club" : "Create club"}
              </Button>
            </div>
          ))}
        </Card>
      )}

      {existing.length > 0 && (
        <div className="mt-6">
          <h4 className="mb-2 text-sm font-semibold">Already-created custom clubs</h4>
          <Card className="divide-y divide-border border-border bg-card">
            {existing.map((c) => (
              <CustomClubRow key={c._id} club={c} onChange={load} />
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function CustomClubRow({ club, onChange }) {
  const [name, setName] = useState(club.name || "");
  const [saving, setSaving] = useState(false);
  const [cover, setCover] = useState(club.cover_image || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const dirty = name.trim() && name.trim() !== club.name;

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await mediaAPI.upload(file);
      const url = r.data.url;
      await adminSimhubAPI.updateCustomClub(club._id, { cover_image: url });
      setCover(url);
      toast.success("Club image updated");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload image");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminSimhubAPI.updateCustomClub(club._id, { name: name.trim() });
      toast.success("Club renamed");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rename");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${club.name}"?`)) return;
    try {
      await adminSimhubAPI.deleteCustomClub(club._id);
      toast.success("Club deleted");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-2 p-3 md:grid-cols-[auto_1fr_auto] md:items-center" data-testid={`admin-custom-club-${club._id}`}>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary bg-cover bg-center"
        style={{ backgroundImage: cover ? `url(${resolveMediaUrl(cover)})` : undefined }}
        data-testid={`admin-custom-club-image-${club._id}`}
        title="Upload club image"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : !cover && <Upload className="h-4 w-4 text-muted-foreground" />}
      </button>
      <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={uploadImage} data-testid={`admin-custom-club-image-input-${club._id}`} />
      <div className="min-w-0">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="max-w-md"
          data-testid={`admin-custom-club-name-${club._id}`}
        />
        <p className="mt-1 truncate text-xs text-muted-foreground font-mono">{club.kind} · {club.key}</p>
      </div>
      <div className="flex gap-2 md:justify-end">
        <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`admin-custom-club-save-${club._id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Save</>}
        </Button>
        <Button size="sm" variant="destructive" onClick={remove} data-testid={`admin-custom-club-delete-${club._id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}



/* ---------------- Forum Spotlight ---------------- */
const SPOTLIGHT_TYPES = [
  { id: "sponsorship_cta", label: "Sponsorship CTA" },
  { id: "hot_marketplace", label: "Hot marketplace listing" },
  { id: "hot_social", label: "Hot social post" },
  { id: "hot_forum", label: "Hot forum post" },
  { id: "hot_curated", label: "Hot curated item" },
  { id: "custom_cta", label: "Custom CTA" },
];

function SpotlightSlotRow({ slot, index, total, reload, onMove }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: slot.type,
    enabled: !!slot.enabled,
    custom_title: slot.custom_title || "",
    custom_body: slot.custom_body || "",
    custom_image_url: slot.custom_image_url || "",
    custom_cta_label: slot.custom_cta_label || "",
    custom_cta_url: slot.custom_cta_url || "",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await forumSpotlightAdminAPI.update(slot.id, form);
      toast.success("Slot saved");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const remove = () => act(() => forumSpotlightAdminAPI.remove(slot.id), "Slot deleted", reload);

  const isCustom = form.type === "custom_cta";

  return (
    <Card className="space-y-3 border-border bg-card p-4" data-testid={`admin-spotlight-slot-${slot.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-secondary px-2 py-0.5 text-xs font-semibold">Slot #{index + 1}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`admin-spotlight-move-up-${slot.id}`}>↑</Button>
          <Button size="sm" variant="outline" disabled={index === total - 1} onClick={() => onMove(index, 1)} data-testid={`admin-spotlight-move-down-${slot.id}`}>↓</Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Enabled</span>
            <Switch checked={form.enabled} onCheckedChange={set("enabled")} data-testid={`admin-spotlight-enabled-${slot.id}`} />
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Type</label>
          <Select value={form.type} onValueChange={set("type")}>
            <SelectTrigger data-testid={`admin-spotlight-type-${slot.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {SPOTLIGHT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isCustom && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Title</label>
            <Input value={form.custom_title} onChange={(e) => set("custom_title")(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">CTA label</label>
            <Input value={form.custom_cta_label} onChange={(e) => set("custom_cta_label")(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Body</label>
            <Input value={form.custom_body} onChange={(e) => set("custom_body")(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Image</label>
            <div className="flex flex-wrap items-center gap-2">
              <Input value={form.custom_image_url} onChange={(e) => set("custom_image_url")(e.target.value)} placeholder="Paste image URL or upload" className="flex-1 min-w-[240px]" data-testid={`admin-spotlight-image-url-${slot.id}`} />
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-xs hover:border-primary/60" data-testid={`admin-spotlight-image-upload-${slot.id}`}>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    try {
                      const r = await forumSpotlightAdminAPI.uploadImage(f);
                      if (r.data?.url) {
                        set("custom_image_url")(r.data.url);
                        toast.success("Image uploaded");
                      } else {
                        toast.error("Upload failed");
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Upload failed");
                    }
                  }}
                />
                Upload
              </label>
              {form.custom_image_url && (
                <img src={form.custom_image_url} alt="" className="h-10 w-auto rounded border border-border object-contain" />
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">CTA URL</label>
            <Input value={form.custom_cta_url} onChange={(e) => set("custom_cta_url")(e.target.value)} placeholder="/marketplace or https://..." />
          </div>
        </div>
      )}

      {!isCustom && (
        <p className="text-xs text-muted-foreground">
          Auto-rotates the top {10} items on each page load. No manual item pick needed.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`admin-spotlight-save-${slot.id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Save</>}
        </Button>
        <Button size="sm" variant="destructive" onClick={remove} data-testid={`admin-spotlight-delete-${slot.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

export function AdminForumSpotlight() {
  const { data, loading, reload } = useList(() => forumSpotlightAdminAPI.list().then((r) => r.data?.slots || []));
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      await forumSpotlightAdminAPI.create({ type: "custom_cta", enabled: true });
      toast.success("Slot added");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add slot");
    } finally { setCreating(false); }
  };

  const move = async (index, delta) => {
    const next = [...data];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    const order = next.map((s) => s.id);
    try {
      await forumSpotlightAdminAPI.reorder(order);
      reload();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-forum-spotlight">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">Forum Spotlight</h2>
          <p className="text-xs text-muted-foreground">Add, reorder, and configure the sticky promo rail shown next to the forum.</p>
        </div>
        <Button size="sm" onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-spotlight-add">
          <Plus className="mr-1 h-4 w-4" /> Add slot
        </Button>
      </div>
      {loading ? <Loading /> : !data.length ? <Empty text="No spotlight slots yet. Add one to start." /> : (
        <div className="space-y-3">
          {data.map((slot, i) => (
            <SpotlightSlotRow key={slot.id} slot={slot} index={i} total={data.length} reload={reload} onMove={move} />
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------------- Sponsor Pool (admin-managed sponsor logos) ---------------- */
function SponsorPoolRow({ s, reload }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: s.name, logo_url: s.logo_url, website: s.website || "" });
  const save = async () => {
    try {
      await sponsorAdminAPI.updateSponsor(s.id, form);
      toast.success("Sponsor saved");
      setEditing(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = () => act(() => sponsorAdminAPI.deleteSponsor(s.id), "Sponsor removed", reload);
  return (
    <Card className="flex items-center gap-3 border-border bg-card p-3" data-testid={`admin-sponsor-row-${s.id}`}>
      <img src={s.logo_url} alt={s.name} className="h-10 w-20 rounded bg-background object-contain" />
      {editing ? (
        <div className="grid flex-1 gap-2 sm:grid-cols-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="Logo URL" />
          <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" />
        </div>
      ) : (
        <div className="flex-1">
          <p className="font-semibold">{s.name}</p>
          <p className="truncate text-xs text-muted-foreground">{s.website || s.logo_url}</p>
        </div>
      )}
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
            <Button size="sm" variant="destructive" onClick={remove}><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </div>
    </Card>
  );
}

export function AdminSponsors() {
  const { data, loading, reload } = useList(() => sponsorAdminAPI.listSponsors().then((r) => r.data || []));
  const [form, setForm] = useState({ name: "", logo_url: "", website: "" });
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!form.name.trim() || !form.logo_url.trim()) {
      toast.error("Name and logo URL are required");
      return;
    }
    setCreating(true);
    try {
      await sponsorAdminAPI.createSponsor(form);
      toast.success("Sponsor added");
      setForm({ name: "", logo_url: "", website: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add sponsor");
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sponsors-pool">
      <div>
        <h2 className="font-display text-lg font-semibold">Sponsor logos</h2>
        <p className="text-xs text-muted-foreground">
          Add sponsors here, then attach them to a specific approved sponsorship application below. Logos appear on that user's public sessions.
        </p>
      </div>

      <Card className="space-y-3 border-border bg-card p-4">
        <p className="font-display text-xs font-bold uppercase tracking-wide">Add a sponsor</p>
        <div className="grid gap-2 sm:grid-cols-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sponsor name" data-testid="admin-sponsor-name" />
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="Logo URL" data-testid="admin-sponsor-logo-url" />
          <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" data-testid="admin-sponsor-website" />
        </div>
        <Button size="sm" onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-sponsor-add">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add sponsor</>}
        </Button>
      </Card>

      {loading ? <Loading /> : !data.length ? <Empty text="No sponsors yet." /> : (
        <div className="space-y-2">
          {data.map((s) => <SponsorPoolRow key={s.id} s={s} reload={reload} />)}
        </div>
      )}

      <AdminAssignSponsorsPanel sponsors={data} />
    </div>
  );
}

function AdminAssignSponsorsPanel({ sponsors }) {
  const { data: requests, loading, reload } = useList(() =>
    adminAPI.getSponsorships({ limit: 100 }).then((r) => r.data?.sponsorships || r.data?.requests || (Array.isArray(r.data) ? r.data : [])),
  );
  const [q, setQ] = useState("");
  const approved = (requests || []).filter((r) => ["approved", "active"].includes(r.status));
  const term = q.trim().toLowerCase();
  const filtered = term
    ? approved.filter((r) =>
        [r.user_username, r.real_name, r.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
      )
    : approved;
  return (
    <div className="space-y-2">
      <p className="font-display text-xs font-bold uppercase tracking-wide">Attach sponsors to a user</p>
      {loading ? <Loading /> : !approved.length ? <Empty text="No approved sponsorships yet." /> : (
        <>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sponsored users to assign a logo..."
              className="pl-9"
              data-testid="admin-assign-search"
            />
          </div>
          {!filtered.length ? (
            <Empty text="No sponsored users match your search." />
          ) : (
            <div className="space-y-2">
              {filtered.map((req) => (
                <SponsorAssignmentRow key={req.id} req={req} allSponsors={sponsors} reload={reload} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SponsorAssignmentRow({ req, allSponsors, reload }) {
  const [picked, setPicked] = useState(Array.isArray(req.sponsors) ? req.sponsors : []);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const save = async () => {
    setSaving(true);
    try {
      await sponsorAdminAPI.assignSponsors(req.id, picked);
      toast.success("Sponsors updated");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };
  return (
    <Card className="space-y-2 border-border bg-card p-3" data-testid={`admin-assign-${req.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{req.real_name || req.user_username} <span className="text-xs text-muted-foreground">@{req.user_username}</span></p>
          <p className="text-[11px] text-muted-foreground capitalize">{req.status}</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {allSponsors.map((s) => {
          const on = picked.includes(s.id);
          return (
            <button key={s.id} type="button" onClick={() => toggle(s.id)}
              className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${on ? "border-primary bg-primary/10" : "border-border bg-background"}`}
              data-testid={`admin-assign-${req.id}-${s.id}`}
            >
              <img src={s.logo_url} alt={s.name} className="h-4 w-auto object-contain" />
              {s.name}
            </button>
          );
        })}
      </div>
    </Card>
  );
}


/* ---------------- Staff access / per-tab permissions ---------------- */
export function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    adminAPI.listStaff()
      .then((r) => { setStaff(r.data?.staff || []); setTabs(r.data?.tabs || []); })
      .catch(() => { setStaff([]); setTabs([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  if (loading) return <Loading />;

  const owners = staff.filter((s) => s.is_super_admin);
  const members = staff.filter((s) => !s.is_super_admin);

  return (
    <div className="space-y-5" data-testid="admin-staff">
      <div>
        <h2 className="font-display text-lg font-semibold">Staff access</h2>
        <p className="text-xs text-muted-foreground">
          Grant trusted members access to specific admin tools. Only the checked tabs are visible and usable for each staff member.
        </p>
      </div>

      <AddStaffMember staff={staff} onAdded={reload} />

      {owners.length > 0 && (
        <div className="space-y-2">
          <p className="font-display text-xs font-bold uppercase tracking-wide">Owners</p>
          {owners.map((o) => (
            <Card key={o.id} className="flex items-center gap-3 border-border bg-card p-3" data-testid={`admin-staff-owner-${o.id}`}>
              <UserCog className="h-4 w-4 text-primary" />
              <div className="min-w-0">
                <p className="truncate font-medium">{o.username}</p>
                <p className="text-xs text-muted-foreground">{o.email}</p>
              </div>
              <Badge className="ml-auto border-0 bg-primary/15 text-primary">Full access</Badge>
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="font-display text-xs font-bold uppercase tracking-wide">Staff members</p>
        {!members.length ? (
          <Empty text="No staff members yet. Add one above." />
        ) : (
          members.map((m) => <StaffRow key={m.id} member={m} tabs={tabs} onChange={reload} />)
        )}
      </div>
    </div>
  );
}

function AddStaffMember({ staff, onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const existingIds = new Set(staff.map((s) => s.id));

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await adminAPI.getUsers({ search: q.trim(), limit: 20 });
      setResults((r.data?.users || []).filter((u) => !existingIds.has(u.id)));
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const add = async (u) => {
    await act(() => adminAPI.setStaffPermissions(u.id, { is_staff: true, permissions: [] }), `${u.username} added as staff`, () => { setResults([]); setQ(""); onAdded?.(); });
  };

  return (
    <Card className="space-y-3 border-border bg-card p-4">
      <p className="font-display text-xs font-bold uppercase tracking-wide">Add a staff member</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search users by name or email..."
            className="pl-9"
            data-testid="admin-staff-search"
          />
        </div>
        <Button size="sm" onClick={search} disabled={searching} data-testid="admin-staff-search-btn">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </div>
      {results.length > 0 && (
        <div className="space-y-1">
          {results.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2" data-testid={`admin-staff-result-${u.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{u.username} {u.is_admin && <Badge className="ml-1 border-0 bg-secondary text-xs">admin</Badge>}</p>
                <p className="truncate text-xs text-muted-foreground">{u.email}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => add(u)} data-testid={`admin-staff-add-${u.id}`}>
                <UserPlus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function StaffRow({ member, tabs, onChange }) {
  const allIds = tabs.map((t) => t.id);
  const initial = member.staff_permissions === null ? allIds : member.staff_permissions;
  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);
  const fullLegacy = member.staff_permissions === null;

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const setAll = (on) => setSelected(on ? allIds : []);

  const save = async () => {
    setSaving(true);
    await act(() => adminAPI.setStaffPermissions(member.id, { is_staff: true, permissions: selected }), "Permissions saved", onChange);
    setSaving(false);
  };
  const removeAccess = async () => {
    await act(() => adminAPI.setStaffPermissions(member.id, { is_staff: false, permissions: [] }), `${member.username} removed from staff`, onChange);
  };

  return (
    <Card className="space-y-3 border-border bg-card p-4" data-testid={`admin-staff-member-${member.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium">{member.username}</p>
          <p className="truncate text-xs text-muted-foreground">{member.email}</p>
        </div>
        <Button size="sm" variant="outline" onClick={removeAccess} className="border-destructive/50 text-destructive hover:bg-destructive/10" data-testid={`admin-staff-remove-${member.id}`}>
          Remove access
        </Button>
      </div>

      {fullLegacy && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-300">
          Currently has full access. Uncheck tabs below and Save to restrict this member.
        </p>
      )}

      <div className="flex items-center gap-2 text-xs">
        <button type="button" onClick={() => setAll(true)} className="rounded border border-border px-2 py-1 hover:bg-secondary/60" data-testid={`admin-staff-all-${member.id}`}>Select all</button>
        <button type="button" onClick={() => setAll(false)} className="rounded border border-border px-2 py-1 hover:bg-secondary/60" data-testid={`admin-staff-none-${member.id}`}>Clear all</button>
        <span className="text-muted-foreground">{selected.length} of {allIds.length} tabs</span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tabs.map((t) => {
          const on = selected.includes(t.id);
          return (
            <label key={t.id} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${on ? "border-primary bg-primary/10" : "border-border bg-background"}`} data-testid={`admin-staff-${member.id}-tab-${t.id}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(t.id)} className="accent-primary" />
              <span className="truncate">{t.label}</span>
            </label>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`admin-staff-save-${member.id}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save permissions</>}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Legal pages (Privacy / Terms) block editor
// ---------------------------------------------------------------------------
const BLOCK_TYPES = [
  { value: "h1", label: "Large heading" },
  { value: "h2", label: "Medium heading" },
  { value: "h3", label: "Small heading" },
  { value: "paragraph", label: "Body text" },
];

export function AdminLegal() {
  const [slug, setSlug] = useState("privacy");
  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = (s) => {
    setLoading(true);
    legalAPI.get(s).then((r) => {
      setTitle(r.data?.title || "");
      setBlocks(r.data?.blocks || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(slug); }, [slug]);

  const updBlock = (i, patch) => setBlocks((b) => b.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addBlock = () => setBlocks((b) => [...b, { type: "paragraph", text: "" }]);
  const removeBlock = (i) => setBlocks((b) => b.filter((_, idx) => idx !== i));
  const move = (i, dir) => setBlocks((b) => {
    const j = i + dir;
    if (j < 0 || j >= b.length) return b;
    const copy = [...b];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateLegal(slug, { title, blocks });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["privacy", "terms"].map((s) => (
          <Button key={s} size="sm" variant={slug === s ? "default" : "outline"} onClick={() => setSlug(s)} data-testid={`admin-legal-tab-${s}`}>
            {s === "privacy" ? "Privacy Policy" : "Terms of Service"}
          </Button>
        ))}
      </div>
      {loading ? <Loading /> : (
        <>
          <div className="space-y-2">
            <Label>Page title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-legal-title" />
          </div>
          <div className="space-y-3">
            {blocks.map((b, i) => (
              <Card key={i} className="space-y-2 border-border bg-card p-3">
                <div className="flex items-center gap-2">
                  <Select value={b.type} onValueChange={(v) => updBlock(i, { type: v })}>
                    <SelectTrigger className="h-8 w-44 text-sm" data-testid={`admin-legal-block-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BLOCK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => move(i, -1)}>↑</Button>
                    <Button size="sm" variant="ghost" onClick={() => move(i, 1)}>↓</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeBlock(i)}>Remove</Button>
                  </div>
                </div>
                <Textarea rows={b.type === "paragraph" ? 4 : 1} value={b.text} onChange={(e) => updBlock(i, { text: e.target.value })} data-testid={`admin-legal-block-text-${i}`} />
              </Card>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addBlock} data-testid="admin-legal-add-block">+ Add block</Button>
            <Button onClick={save} disabled={saving} data-testid="admin-legal-save">{saving ? "Saving..." : "Save page"}</Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact form submissions
// ---------------------------------------------------------------------------
export function AdminContact() {
  const { data, loading, reload } = useList(() => adminAPI.getContactSubmissions().then((r) => r.data || []));
  if (loading) return <Loading />;
  if (!data.length) return <p className="text-sm text-muted-foreground">No contact submissions yet.</p>;
  return (
    <div className="space-y-2">
      {data.map((s) => (
        <Card key={s.id} className={`space-y-1 border-border p-3 ${s.is_read ? "bg-card" : "bg-primary/[0.06]"}`} data-testid={`admin-contact-${s.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">{s.first_name} {s.last_name} {!s.is_read && <Badge className="ml-1 border-0 bg-primary/20 text-primary">New</Badge>}</p>
              <p className="text-xs text-muted-foreground">{s.email}{s.phone ? ` · ${s.phone}` : ""}{s.company ? ` · ${s.company}` : ""}</p>
              {s.website && <p className="text-xs text-primary/80">{s.website}</p>}
            </div>
            <span className="flex-shrink-0 text-xs text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{s.message}</p>
          {!s.is_read && (
            <Button size="sm" variant="outline" onClick={() => adminAPI.markContactRead(s.id).then(reload)} data-testid={`admin-contact-read-${s.id}`}>Mark read</Button>
          )}
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Moderation queue ---------------- */
const SOURCE_META = {
  automated_block: { label: "Auto block", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  automated_soft_flag: { label: "Soft flag", cls: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  probation_hold: { label: "Probation hold", cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  member_report: { label: "Member report", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
  filter_down: { label: "Filter down", cls: "border-orange-500/40 bg-orange-500/10 text-orange-300" },
};

export function AdminModeration() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [source, setSource] = useState("all");
  const [surface, setSurface] = useState("all");
  const [msgFor, setMsgFor] = useState(null);
  const [msgText, setMsgText] = useState("");

  const loadSettings = () => moderationAPI.getSettings().then((r) => setSettings(r.data)).catch(() => {});
  const loadStats = () => moderationAPI.analytics().then((r) => setStats(r.data)).catch(() => {});

  const { data, loading, reload } = useList(
    () => moderationAPI.listItems({
      ...(source !== "all" ? { source } : {}),
      ...(surface !== "all" ? { surface } : {}),
    }).then((r) => r.data.items),
    [source, surface]
  );

  useEffect(() => { loadSettings(); loadStats(); }, []);

  const reloadAll = () => { reload(); loadStats(); };

  const saveSettings = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    act(() => moderationAPI.updateSettings(patch), "Settings saved", loadSettings);
  };

  const sendMessage = (id) => {
    if (!msgText.trim()) return;
    act(() => moderationAPI.messageAuthor(id, msgText.trim()), "Message sent", () => { setMsgFor(null); setMsgText(""); });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Moderation</h2>
        <p className="text-sm text-muted-foreground">Review held, flagged, and member-reported posts.</p>
      </div>

      {/* Analytics */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Open items", stats.open_items],
            ["Approved", stats.approved],
            ["Removed", stats.removed],
            ["Member reports", stats.member_reports],
          ].map(([k, v]) => (
            <Card key={k} className="border-border bg-card p-3" data-testid={`mod-stat-${k}`}>
              <p className="font-display text-xl font-bold text-primary">{v ?? 0}</p>
              <p className="text-xs text-muted-foreground">{k}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Settings */}
      {settings && (
        <Card className="space-y-4 border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">New user probation</p>
              <p className="text-xs text-muted-foreground">When on, every new member's posts are held until approved. Ships off.</p>
            </div>
            <Switch checked={!!settings.probation_enabled} onCheckedChange={(v) => saveSettings({ probation_enabled: v })} data-testid="mod-probation-toggle" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Approved posts to graduate</Label>
              <Input type="number" min={0} value={settings.graduation_min_approved_posts}
                onChange={(e) => setSettings({ ...settings, graduation_min_approved_posts: Number(e.target.value) })}
                onBlur={(e) => saveSettings({ graduation_min_approved_posts: Number(e.target.value) })}
                data-testid="mod-min-posts" />
            </div>
            <div>
              <Label className="text-xs">Days since joining</Label>
              <Input type="number" min={0} value={settings.graduation_min_days}
                onChange={(e) => setSettings({ ...settings, graduation_min_days: Number(e.target.value) })}
                onBlur={(e) => saveSettings({ graduation_min_days: Number(e.target.value) })}
                data-testid="mod-min-days" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Allow words (comma separated)</Label>
              <Input defaultValue={(settings.allow_words || []).join(", ")}
                onBlur={(e) => saveSettings({ allow_words: e.target.value.split(",").map((w) => w.trim()).filter(Boolean) })}
                data-testid="mod-allow-words" />
            </div>
            <div>
              <Label className="text-xs">Deny words (comma separated)</Label>
              <Input defaultValue={(settings.deny_words || []).join(", ")}
                onBlur={(e) => saveSettings({ deny_words: e.target.value.split(",").map((w) => w.trim()).filter(Boolean) })}
                data-testid="mod-deny-words" />
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-5">
            {Object.keys(settings.category_strictness || {}).map((cat) => (
              <div key={cat}>
                <Label className="text-[10px] uppercase text-muted-foreground">{cat.replace(/_/g, " ")}</Label>
                <Select value={settings.category_strictness[cat]} onValueChange={(v) => saveSettings({ category_strictness: { ...settings.category_strictness, [cat]: v } })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["off", "lenient", "normal", "strict"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {/* Post edit & delete windows (item 12) */}
          <div className="border-t border-border pt-4">
            <p className="text-sm font-semibold">Member edit &amp; delete windows</p>
            <p className="mb-3 text-xs text-muted-foreground">How many days members can edit or delete their own posts. Turn on &quot;Open&quot; to allow it any time with no limit.</p>
            <div className="space-y-4">
              {["marketplace", "forum", "social"].map((cat) => {
                const rule = (settings.post_rules || {})[cat] || {};
                const setRule = (patch) => saveSettings({ post_rules: { ...(settings.post_rules || {}), [cat]: { ...rule, ...patch } } });
                return (
                  <div key={cat} className="rounded-md border border-border p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">{cat}</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {["edit", "delete"].map((action) => (
                        <div key={action} className="space-y-2 rounded-md bg-secondary/30 p-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs capitalize">{action} window</Label>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-muted-foreground">Open</span>
                              <Switch
                                checked={!!rule[`${action}_open`]}
                                onCheckedChange={(v) => setRule({ [`${action}_open`]: v })}
                                data-testid={`mod-${cat}-${action}-open`}
                              />
                            </div>
                          </div>
                          {!rule[`${action}_open`] && (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number" min={0} className="h-8 w-20 text-xs"
                                value={rule[`${action}_days`] ?? 0}
                                onChange={(e) => setSettings({ ...settings, post_rules: { ...(settings.post_rules || {}), [cat]: { ...rule, [`${action}_days`]: Number(e.target.value) } } })}
                                onBlur={(e) => setRule({ [`${action}_days`]: Number(e.target.value) })}
                                data-testid={`mod-${cat}-${action}-days`}
                              />
                              <span className="text-xs text-muted-foreground">day(s)</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44" data-testid="mod-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {Object.keys(SOURCE_META).map((s) => <SelectItem key={s} value={s}>{SOURCE_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={surface} onValueChange={setSurface}>
          <SelectTrigger className="w-40" data-testid="mod-filter-surface"><SelectValue placeholder="Surface" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All surfaces</SelectItem>
            <SelectItem value="marketplace">Marketplace</SelectItem>
            <SelectItem value="forum">Forum</SelectItem>
            <SelectItem value="social">Social</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Queue */}
      {loading ? <Loading /> : data.length === 0 ? <Empty text="Nothing in the queue. All clear." /> : (
        <div className="space-y-3">
          {data.map((it) => {
            const meta = SOURCE_META[it.source] || { label: it.source, cls: "border-border bg-secondary text-muted-foreground" };
            return (
              <Card key={it.id} className="space-y-3 border-border bg-card p-4" data-testid={`mod-item-${it.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                  <Badge variant="outline" className="capitalize">{it.surface}</Badge>
                  <span className="text-xs text-muted-foreground">by {it.author_username}</span>
                  {it.report_count > 0 && <span className="text-xs text-rose-400">{it.report_count} report(s)</span>}
                </div>
                {it.reason && <p className="text-sm text-foreground/85"><span className="font-semibold">Reason:</span> {it.reason}</p>}
                {it.text_snapshot && <p className="whitespace-pre-wrap rounded bg-secondary/40 p-2 text-sm text-foreground/80">{it.text_snapshot}</p>}
                {it.images_snapshot?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {it.images_snapshot.map((img, i) => <img key={i} src={img} alt="" className="h-20 w-20 rounded object-cover" />)}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => act(() => moderationAPI.approve(it.id), "Approved", reloadAll)} data-testid={`mod-approve-${it.id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => act(() => moderationAPI.hide(it.id), "Hidden until edited", reloadAll)} data-testid={`mod-hide-${it.id}`}><ShieldOff className="mr-1 h-4 w-4" /> Hide</Button>
                  <Button size="sm" variant="destructive" onClick={() => act(() => moderationAPI.remove(it.id), "Removed", reloadAll)} data-testid={`mod-remove-${it.id}`}><Trash2 className="mr-1 h-4 w-4" /> Remove</Button>
                  <Button size="sm" variant="outline" onClick={() => { setMsgFor(msgFor === it.id ? null : it.id); setMsgText(""); }} data-testid={`mod-message-${it.id}`}><MessageSquare className="mr-1 h-4 w-4" /> Message</Button>
                </div>
                {msgFor === it.id && (
                  <div className="flex gap-2">
                    <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Message to the author" data-testid={`mod-message-input-${it.id}`} />
                    <Button size="sm" onClick={() => sendMessage(it.id)}><Send className="h-4 w-4" /></Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
